// BisListModal.tsx
// In-game character sheet: BIS list · Battle.net equipment · loot history.
//
// The paper doll is the interface. Every socket shows exactly ONE item — the
// top-priority target — coloured by how far along that socket is. Everything
// else about a slot stays hidden until the player opens its panel, which
// carries the full saved list plus the loot table filtered by raid and boss.
//
// Layout (BIS + Equipped tabs share it):
//   • LEFT rail — a work queue, one line per socket, filtered by status.
//     Never a second copy of the list.
//   • CENTRE — the doll: eight sockets a side around the character render,
//     weapons beneath. A "+N" badge marks options hidden behind a socket.
//   • SLOT PANEL — opened on request; saved targets on the left (ordered,
//     retaggable), the raid/boss-filtered loot table on the right.
//
// Slot handling lives entirely in lib/gearSlots: a group owns one or more
// sockets, and `seatTargets` decides which saved item sits where. That is
// what makes rings and off-hands ordinary — two interchangeable ring
// sockets, and a weapons group whose capacity depends on what you picked
// (a two-hander spans both sockets, a shield only fits the second).
//
// Read-only mode: when canEdit=false the picker is hidden and the doll just
// displays the character's existing entries.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { API_URL } from "./config";
import { wowheadItemUrl, refreshWowheadTooltips } from "./wowhead";
import { getWowClassColor } from "./wowClassColor";
import AddonExportPanel from "./AddonExportPanel";
import {
  GearGroup,
  GearSocket,
  Seating,
  groupsForGame,
  planningGroups,
  groupById,
  groupsForColumn,
  groupAcceptsItem,
  socketAcceptsItem,
  normalizeSlot,
  seatTargets,
  seatEquipped,
  equippedForGroup,
} from "./lib/gearSlots";

// ── Types ─────────────────────────────────────────────────────

type Tag = "bis" | "upgrade" | "sidegrade";

type LootItem = {
  id: number;
  external_item_id: string;
  name: string;
  quality: string | null;
  slot: string | null;
  boss_name: string | null;
  metadata: any;
  raid_option_id?: number | null;
  /** `game_option.label` for the source raid — drives the picker's raid filter. */
  raid_name?: string | null;
  redeemed_from_id?: number | null;
  redeemed_from_name?: string | null;
  redeemed_from_boss?: string | null;
  redeemed_from_external_id?: string | null;
};

type BisEntry = {
  id: string;
  character_id: string;
  loot_item_id: number;
  tag: Tag;
  priority: number;
  /**
   * Flat rank across the whole list, set on the Prio tab. Only the addon
   * exports read it — `priority` is what orders a slot on the doll, and the
   * two are deliberately independent.
   */
  prio_order: number | null;
  note: string | null;
  created_at: string;
  updated_at: string;
  external_item_id: string;
  item_name: string;
  item_quality: string | null;
  boss_name: string | null;
  item_metadata: any;
  slot: string | null;
  raid_option_id?: number | null;
  raid_name?: string | null;
  redeemed_from_id?: number | null;
  redeemed_from_name?: string | null;
  redeemed_from_boss?: string | null;
  redeemed_from_external_id?: string | null;
};

type EquippedPiece = {
  slot_type: string;
  item_id: number;
  name: string;
  quality: string | null;
};

type CharacterStub = {
  id: string;
  name: string;
  game_id: string;
  game_code?: string | null;
  class_name: string | null;
  /** From `app_user_character.metadata.equipment` after BNet sync */
  equipment?: { items: EquippedPiece[] };
  equipment_last_updated?: string | null;
  /** Which Battle.net profile namespace supplied equipment (e.g. classic, classicann, retail) */
  bnet_equipment_profile_ns?: string | null;
  /** Battle.net character render (`metadata.render_url`) — shown in the paper-doll centre. */
  render_url?: string | null;
  /** `metadata.primary_spec_label` — shown next to the class in the doll header. */
  spec_label?: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  character: CharacterStub;
  /** When false, the modal renders read-only (no picker, no edit buttons). */
  canEdit: boolean;
  /** Guild scope for loot history (Step 3). Omit to hide the Loot tab. */
  guildId?: string | null;
  showToast?: (message: string, type?: "success" | "error" | "info") => void;
};

// ── That's My BIS import ──────────────────────────────────────

type ImportMatch = {
  external_item_id: string;
  loot_item_id: number;
  item_name: string;
  item_quality: string | null;
  boss_name: string | null;
  slot: string | null;
  tag: Tag;
  priority: number;
  offspec: boolean;
  source_type: "wishlist" | "prio" | "received";
  /** Already on this character's list, so importing would leave it untouched. */
  already_on_list: boolean;
};

type ImportUnmatched = {
  external_item_id: string;
  item_name?: string;
  reason: "not_in_loot_table" | "missing_slot_data";
};

type ImportPreview = {
  format: "gargul" | "csv" | "items";
  /** The export names several characters and none of them is obviously ours. */
  needs_character: boolean;
  selected_character: string | null;
  characters: { name: string; count: number }[];
  matched: ImportMatch[];
  unmatched: ImportUnmatched[];
  counts: { matched: number; unmatched: number; duplicates: number };
};

const IMPORT_ERRORS: Record<string, string> = {
  empty_paste: "Paste your export first.",
  unrecognized_format:
    "That does not look like a That's My BIS export. Paste the Gargul export, a wishlist CSV, or a list of item links.",
  paste_too_large: "That paste is too big. Export just your guild's wishlists rather than the full history.",
  not_your_character: "You can only import onto your own characters.",
};

type LootAwardRow = {
  id: string;
  external_item_id: string;
  loot_item_id: number | null;
  awarded_at: string;
  source: string;
  response: string | null;
  bis_tag_at_award: string | null;
  raw: Record<string, unknown> | null;
  item_name: string | null;
  item_quality: string | null;
};

// ── Paper-doll layout ─────────────────────────────────────────
//
// Sockets come straight from the shared gear-slot table, so the doll's
// arrangement, the rail's order and the picker's eligibility all read from
// one source. A "position" is just a socket plus the group that owns it.

type DollPos = {
  /** Socket key, unique across the doll ("ring1", "offhand"). */
  key: string;
  group: GearGroup;
  socket: GearSocket;
  /** Index of this socket within its group — which seat to read. */
  index: number;
  label: string;
  cosmetic?: boolean;
};

function positionsForColumn(gameCode: string | null, column: "left" | "right" | "bottom"): DollPos[] {
  const out: DollPos[] = [];
  for (const group of groupsForColumn(gameCode, column)) {
    group.sockets.forEach((socket, index) => {
      out.push({
        key: socket.key,
        group,
        socket,
        index,
        label: socket.label,
        cosmetic: group.cosmetic,
      });
    });
  }
  return out;
}

function allPositions(gameCode: string | null): DollPos[] {
  return [
    ...positionsForColumn(gameCode, "left"),
    ...positionsForColumn(gameCode, "right"),
    ...positionsForColumn(gameCode, "bottom"),
  ];
}

/** One paper-doll tile's contents, whatever the data source (BIS list or Battle.net). */
type DollItemView = {
  externalItemId: string | number;
  name: string;
  qualityKey: string;
  /** Small caps line under the name ("BIS", "Upgrade", boss name…). */
  note?: string | null;
  noteClass?: string;
} | null;

type DollSlotView = {
  item: DollItemView;
  /** Saved options this group holds that no socket could seat. */
  extra: number;
  /** This socket is covered by a spanning item seated in an earlier socket. */
  coveredBy?: string | null;
};

const TAG_STYLES: Record<
  Tag,
  { label: string; pill: string; ring: string; dot: string; /** Second-line tag label on item rows */ subLabel: string }
> = {
  bis: {
    label: "BIS",
    pill: "bg-amber-500/15 text-amber-200 border-amber-500/35 shadow-[inset_0_1px_0_rgba(251,191,36,0.12)]",
    ring: "ring-amber-500",
    dot: "bg-amber-400",
    subLabel: "text-amber-200/95",
  },
  upgrade: {
    label: "Upgrade",
    pill: "bg-emerald-500/15 text-emerald-200 border-emerald-500/35 shadow-[inset_0_1px_0_rgba(52,211,153,0.1)]",
    ring: "ring-emerald-500",
    dot: "bg-emerald-400",
    subLabel: "text-emerald-300/95",
  },
  sidegrade: {
    label: "Sidegrade",
    pill: "bg-violet-500/12 text-violet-200 border-violet-500/30 shadow-[inset_0_1px_0_rgba(167,139,250,0.08)]",
    ring: "ring-violet-400",
    dot: "bg-violet-400",
    subLabel: "text-violet-200/95",
  },
};

const ALL_TAGS: Tag[] = ["bis", "upgrade", "sidegrade"];

/**
 * The flat order the Prio tab shows and the addons export.
 *
 * Mirrors `orderEntries` in auth/bis_export.js on purpose: anything the player
 * has ranked comes first in that rank, and the rest keeps its BIS-list
 * ordering behind it. A list nobody has touched still opens in a sensible
 * order instead of whatever the database felt like returning.
 */
function sortForPrio(entries: BisEntry[]): BisEntry[] {
  const tagRank: Record<Tag, number> = { bis: 0, upgrade: 1, sidegrade: 2 };
  const ranked = entries.filter((e) => e.prio_order != null);
  const rest = entries.filter((e) => e.prio_order == null);
  ranked.sort((a, b) => (a.prio_order as number) - (b.prio_order as number) || a.id.localeCompare(b.id));
  rest.sort((a, b) => {
    const t = tagRank[a.tag] - tagRank[b.tag];
    if (t !== 0) return t;
    const s = (a.slot || "").localeCompare(b.slot || "");
    if (s !== 0) return s;
    if (a.priority !== b.priority) return a.priority - b.priority;
    return a.created_at.localeCompare(b.created_at);
  });
  return [...ranked, ...rest];
}

/**
 * `raid_loot_item.quality` is sometimes plain text ("4", "epic"), sometimes a Blizzard-style
 * JSON object `{ type: 4 }`. Coerce to a stable string before mapping to a color.
 */
function coerceLootQualityKey(quality: unknown): string {
  if (quality == null) return "";
  if (typeof quality === "number" && Number.isFinite(quality)) return String(Math.trunc(quality));
  if (typeof quality === "string") {
    const s = quality.trim();
    if (!s) return "";
    if (s.startsWith("{") && s.endsWith("}")) {
      try {
        const inner = coerceLootQualityKey(JSON.parse(s) as unknown);
        if (inner) return inner;
      } catch {
        /* not JSON */
      }
    }
    return s;
  }
  if (typeof quality === "object" && quality !== null) {
    const o = quality as Record<string, unknown>;
    const t = o.type;
    if (typeof t === "number" && Number.isFinite(t)) return String(Math.trunc(t));
    if (typeof t === "string") return t.trim();
    const n = o.name;
    if (typeof n === "string") return n.trim();
    const qn = o.quality;
    if (typeof qn === "number" && Number.isFinite(qn)) return String(Math.trunc(qn));
    if (typeof qn === "string") return qn.trim();
  }
  return "";
}

function lootQualityKeyFromMetadata(meta: unknown): string {
  if (!meta || typeof meta !== "object") return "";
  const m = meta as Record<string, unknown>;
  const k0 = coerceLootQualityKey(m.quality);
  if (k0) return k0;
  const objs = [m.item, m.preview_item].filter((x) => x != null && typeof x === "object") as Record<string, unknown>[];
  for (const obj of objs) {
    const k = coerceLootQualityKey(obj.quality);
    if (k) return k;
    const k2 = coerceLootQualityKey(obj);
    if (k2) return k2;
  }
  return "";
}

function lootQualityKeyFromBisEntry(entry: { item_quality: unknown; item_metadata?: unknown }): string {
  let k = coerceLootQualityKey(entry.item_quality);
  if (k) return k;
  k = lootQualityKeyFromMetadata(entry.item_metadata);
  return k;
}

/** BIS list: raid rows missing `quality` still read as epic in-game — avoids a pale “common” bar. */
function bisListRowAccentQualityKey(entry: BisEntry): string {
  const k = lootQualityKeyFromBisEntry(entry);
  if (k) return k;
  return "4";
}

function lootQualityKeyFromLootItem(item: { quality: unknown; metadata?: unknown }): string {
  let k = coerceLootQualityKey(item.quality);
  if (k) return k;
  k = lootQualityKeyFromMetadata(item.metadata);
  return k;
}

/** WoW item quality → hex (numeric IDs from Blizzard + common string forms). */
function itemQualityHex(qualityKey: string): string {
  const q = qualityKey.trim().toLowerCase();
  if (q === "6" || q === "artifact") return "#e6cc80";
  if (q === "5" || q === "legendary") return "#ff8000";
  if (q === "4" || q === "epic") return "#a335ee";
  if (q === "3" || q === "rare") return "#0070dd";
  if (q === "2" || q === "uncommon") return "#1eff00";
  if (q === "1" || q === "common") return "#d8d8d8";
  if (q === "0" || q === "poor") return "#9d9d9d";
  /* Unknown / unmapped — avoid near-white so it is not mistaken for "common". */
  return "#5a6270";
}

/** BIS beats upgrade for display when both tags exist for the same item id. */
function matchEquippedToBis(itemId: number, groupEntries: BisEntry[]): "bis" | "upgrade" | "neutral" {
  const idStr = String(itemId);
  const hits = groupEntries.filter((e) => String(e.external_item_id) === idStr);
  if (hits.some((h) => h.tag === "bis")) return "bis";
  if (hits.some((h) => h.tag === "upgrade")) return "upgrade";
  return "neutral";
}

// ── Slot status ───────────────────────────────────────────────
//
// The doll shows one item per socket — whatever `seatTargets` put there —
// and colours the socket by how far along it is.
//
//   done — that target is already on the character
//   want — a target is seated here, the character isn't wearing it
//   none — this socket has nothing seated
//   na   — this socket cannot be filled: covered by a two-hander, or
//          cosmetic. Excluded from every count.
//
// "done" is deliberately set-based: wearing your second ring target in the
// first finger slot still counts, because ring sockets are interchangeable.

type SlotStatus = "done" | "want" | "none" | "na";

const SLOT_STATUS_META: Record<SlotStatus, { label: string; hex: string; dot: string; text: string }> = {
  done: { label: "Wearing it", hex: "#34d399", dot: "bg-emerald-400", text: "text-emerald-300" },
  want: { label: "Still to get", hex: "#f59e0b", dot: "bg-amber-400", text: "text-amber-300" },
  none: { label: "No target", hex: "#f87171", dot: "bg-red-400", text: "text-red-300" },
  na: { label: "Not applicable", hex: "#4b5563", dot: "bg-white/20", text: "text-white/35" },
};

/** "Karazhan · Prince Malchezaar", "Serpentshrine Cavern · Trash", or whatever we know. */
function sourceLabel(src: { raid_name?: string | null; boss_name: string | null }): string {
  const parts = [src.raid_name, src.boss_name].filter((p): p is string => !!p && p.trim().length > 0);
  return parts.length ? parts.join(" · ") : "Source unknown";
}

const UNKNOWN_RAID = "Other sources";

/** Raids present in a set of items, ordered by how much loot each one holds. */
function raidsInItems(items: LootItem[]): string[] {
  const counts = new Map<string, number>();
  for (const it of items) {
    const key = it.raid_name || UNKNOWN_RAID;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([k]) => k);
}

/** Bosses within one raid. Trash sorts last — players scan for a boss name first. */
function bossesInItems(items: LootItem[]): string[] {
  const seen = new Set<string>();
  for (const it of items) seen.add(it.boss_name || "Unknown");
  return Array.from(seen).sort((a, b) => {
    const at = a.toLowerCase() === "trash" ? 1 : 0;
    const bt = b.toLowerCase() === "trash" ? 1 : 0;
    if (at !== bt) return at - bt;
    return a.localeCompare(b);
  });
}

// ── Component ─────────────────────────────────────────────────

export default function BisListModal({ open, onClose, character, canEdit, guildId, showToast }: Props) {
  const [activeTab, setActiveTab] = useState<"bis" | "prio" | "equipped" | "loot">("bis");

  const [entries, setEntries] = useState<BisEntry[]>([]);
  const [catalog, setCatalog] = useState<LootItem[]>([]);
  const [gameCode, setGameCode] = useState<string | null>(character.game_code ?? null);
  const [loading, setLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The open slot panel. Nothing about a slot beyond its top pick is on
  // screen until the player opens it here.
  const [selectedCellId, setSelectedCellId] = useState<string | null>(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerRaid, setPickerRaid] = useState<string | null>(null);
  const [pickerBoss, setPickerBoss] = useState<string | null>(null);
  const [busyItemId, setBusyItemId] = useState<number | null>(null);
  const [reorderBusy, setReorderBusy] = useState(false);

  // Left rail: which slots to list. "none"/"want" are the two work queues.
  const [railFilter, setRailFilter] = useState<SlotStatus | "all">("all");
  const [showAllEquipped, setShowAllEquipped] = useState(false);

  const [lootAwards, setLootAwards] = useState<LootAwardRow[]>([]);
  const [lootLoading, setLootLoading] = useState(false);
  const [lootError, setLootError] = useState<string | null>(null);

  // Prio tab: the flat order, held locally so a drag redraws instantly and the
  // save is a single write at the end of the gesture rather than one per row.
  const [prioIds, setPrioIds] = useState<string[] | null>(null);
  const [prioBusy, setPrioBusy] = useState(false);

  // That's My BIS import
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [importReplace, setImportReplace] = useState(false);
  // External item ids the player unticked in the preview.
  const [importExcluded, setImportExcluded] = useState<Set<string>>(new Set());

  // ── Data loading ────────────────────────────────────────────

  const loadEntries = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/characters/${character.id}/bis`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
      if (data?.character?.game_code) setGameCode(data.character.game_code);
    } catch (e: any) {
      setError(e?.message || "Failed to load BIS list");
    } finally {
      setLoading(false);
    }
  }, [open, character.id]);

  const loadLootAwards = useCallback(async () => {
    if (!open || !guildId) return;
    setLootLoading(true);
    setLootError(null);
    try {
      const q = new URLSearchParams({ characterId: character.id });
      const res = await fetch(`${API_URL}/guilds/${guildId}/loot-awards?${q}`, {
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setLootAwards(Array.isArray(body.awards) ? body.awards : []);
    } catch (e: any) {
      setLootError(e?.message || "Failed to load loot history");
      setLootAwards([]);
    } finally {
      setLootLoading(false);
    }
  }, [open, guildId, character.id]);

  const loadCatalog = useCallback(async () => {
    if (!open || !canEdit) return;
    setCatalogLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/characters/${character.id}/bis/loot-catalog`,
        { credentials: "include" }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setCatalog(Array.isArray(data?.items) ? data.items : []);
    } catch (e: any) {
      // Catalog failure is non-fatal — user can still see existing entries.
      console.warn("loot catalog load failed", e);
      setCatalog([]);
    } finally {
      setCatalogLoading(false);
    }
  }, [open, canEdit, character.id]);

  useEffect(() => {
    if (open) {
      loadEntries();
      loadCatalog();
    } else {
      setSelectedCellId(null);
      setPickerSearch("");
      setPickerRaid(null);
      setPickerBoss(null);
      setRailFilter("all");
      setShowAllEquipped(false);
      setActiveTab("bis");
    }
  }, [open, loadEntries, loadCatalog]);

  // Esc backs out one layer at a time: slot panel, then the modal itself.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (importOpen) return; // the import panel owns its own dismissal
      if (selectedCellId) {
        setSelectedCellId(null);
        return;
      }
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, selectedCellId, importOpen, onClose]);

  // Every slot panel starts unfiltered — filters are per visit, not sticky.
  useEffect(() => {
    setPickerSearch("");
    setPickerRaid(null);
    setPickerBoss(null);
  }, [selectedCellId]);

  useEffect(() => {
    if (open && guildId) {
      loadLootAwards();
    }
  }, [open, guildId, loadLootAwards]);

  // Re-hydrate Wowhead tooltips after every render that adds new anchors.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(refreshWowheadTooltips, 150);
    return () => clearTimeout(t);
  }, [open, entries, catalog, selectedCellId, pickerSearch, activeTab, character.equipment, lootAwards, importPreview]);

  // ── Derived data ────────────────────────────────────────────

  const positions = useMemo(() => allPositions(gameCode), [gameCode]);

  /** Group id → saved entries in priority order. Tag first, then priority. */
  const entriesByGroup = useMemo(() => {
    const out = new Map<string, BisEntry[]>();
    const tagOrder: Record<Tag, number> = { bis: 0, upgrade: 1, sidegrade: 2 };
    for (const group of groupsForGame(gameCode)) {
      const matching = entries.filter((e) => groupAcceptsItem(group, e.slot));
      matching.sort((a, b) => {
        const t = tagOrder[a.tag] - tagOrder[b.tag];
        if (t !== 0) return t;
        if (a.priority !== b.priority) return a.priority - b.priority;
        return a.created_at.localeCompare(b.created_at);
      });
      out.set(group.id, matching);
    }
    return out;
  }, [entries, gameCode]);

  /** Group id → which saved entry sits in which socket. The single source of truth. */
  const seatingByGroup = useMemo(() => {
    const out = new Map<string, ReturnType<typeof seatTargets<BisEntry>>>();
    for (const group of groupsForGame(gameCode)) {
      out.set(group.id, seatTargets(group, entriesByGroup.get(group.id) ?? [], (e) => e.slot));
    }
    return out;
  }, [entriesByGroup, gameCode]);

  const bnetEquipmentItems = useMemo(
    () =>
      Array.isArray(character.equipment?.items) ? character.equipment!.items : [],
    [character.equipment]
  );

  const equipmentSnapshotLoaded = character.equipment !== undefined;

  /** Group id → equipped pieces seated into the same sockets. */
  const equippedSeatingByGroup = useMemo(() => {
    const out = new Map<string, ReturnType<typeof seatEquipped<EquippedPiece>>>();
    for (const group of groupsForGame(gameCode)) {
      out.set(group.id, seatEquipped(group, equippedForGroup(group, bnetEquipmentItems)));
    }
    return out;
  }, [bnetEquipmentItems, gameCode]);

  /**
   * One status per socket. A socket covered by a spanning item (a two-hander
   * seated in the main hand) reports `na` rather than a hole that can never
   * be filled, and cosmetic sockets do the same.
   */
  const statusByPos = useMemo(() => {
    const out = new Map<string, SlotStatus>();
    for (const pos of positions) {
      if (pos.cosmetic) {
        out.set(pos.key, "na");
        continue;
      }
      const seating = seatingByGroup.get(pos.group.id);
      const entry = seating?.seats[pos.index] ?? null;
      if (seating?.spans && pos.index > 0) {
        out.set(pos.key, "na");
        continue;
      }
      if (!entry) {
        out.set(pos.key, "none");
        continue;
      }
      // Set-based on purpose: wearing your ring-2 target in finger 1 counts.
      const worn = equippedForGroup(pos.group, bnetEquipmentItems).some(
        (p) => String(p.item_id) === String(entry.external_item_id)
      );
      out.set(pos.key, worn ? "done" : "want");
    }
    return out;
  }, [positions, seatingByGroup, bnetEquipmentItems]);

  const statusTotals = useMemo(() => {
    const t: Record<SlotStatus, number> = { done: 0, want: 0, none: 0, na: 0 };
    statusByPos.forEach((s) => t[s]++);
    return t;
  }, [statusByPos]);

  /** Sockets that still have no target — the roadmap, minus anything `na`. */
  const emptySocketLabels = useMemo(() => {
    const seen: string[] = [];
    for (const pos of positions) {
      if (statusByPos.get(pos.key) !== "none") continue;
      const label = pos.group.sockets.length > 1 ? pos.group.label : pos.label;
      if (!seen.includes(label)) seen.push(label);
    }
    return seen;
  }, [positions, statusByPos]);

  const bisRoadmapProgress = useMemo(() => {
    const total = statusTotals.done + statusTotals.want + statusTotals.none;
    return { filled: statusTotals.done + statusTotals.want, total };
  }, [statusTotals]);

  const equipMatch = useMemo(
    () => equipmentMatchBreakdown(gameCode, bnetEquipmentItems, entriesByGroup),
    [gameCode, bnetEquipmentItems, entriesByGroup]
  );

  const equipmentLastUpdatedLabel = useMemo(() => {
    const raw = character.equipment_last_updated;
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(d);
  }, [character.equipment_last_updated]);

  // Paper-doll data sources — one resolver per tab, same tile renderer.
  const resolveBisSlot = useCallback(
    (pos: DollPos): DollSlotView => {
      const seating = seatingByGroup.get(pos.group.id);
      if (!seating) return { item: null, extra: 0 };
      const entry = seating.seats[pos.index] ?? null;
      // A two-hander is drawn once, in the first socket it occupies.
      if (seating.spans && pos.index > 0) {
        return { item: null, extra: 0, coveredBy: entry?.item_name ?? null };
      }
      return {
        item: entry
          ? {
              externalItemId: entry.external_item_id,
              name: entry.item_name,
              qualityKey: bisListRowAccentQualityKey(entry),
              // BIS is the assumed default — only call out the weaker tags.
              note: entry.tag === "bis" ? null : TAG_STYLES[entry.tag].label,
              noteClass: TAG_STYLES[entry.tag].subLabel,
            }
          : null,
        // Alternates hang off the last socket of the group so they're counted once.
        extra: pos.index === pos.group.sockets.length - 1 ? seating.extra.length : 0,
      };
    },
    [seatingByGroup]
  );

  const resolveEquippedSlot = useCallback(
    (pos: DollPos): DollSlotView => {
      const seating = equippedSeatingByGroup.get(pos.group.id);
      const piece = seating?.seats[pos.index] ?? null;
      const extra = pos.index === pos.group.sockets.length - 1 ? seating?.extra.length ?? 0 : 0;
      if (!piece) return { item: null, extra };
      const level = matchEquippedToBis(piece.item_id, entriesByGroup.get(pos.group.id) ?? []);
      return {
        item: {
          externalItemId: piece.item_id,
          name: piece.name,
          qualityKey: coerceLootQualityKey(piece.quality),
          note: level === "bis" ? "On your list · BIS" : level === "upgrade" ? "On your list · Upgrade" : null,
          noteClass: level === "bis" ? TAG_STYLES.bis.subLabel : TAG_STYLES.upgrade.subLabel,
        },
        extra,
      };
    },
    [equippedSeatingByGroup, entriesByGroup]
  );

  const selectedGroup = useMemo(
    () => (selectedCellId ? groupById(gameCode, selectedCellId) : null),
    [selectedCellId, gameCode]
  );

  // loot_item_id → existing BisEntry, for fast picker decoration.
  const entryByItemId = useMemo(() => {
    const m = new Map<number, BisEntry>();
    for (const e of entries) m.set(e.loot_item_id, e);
    return m;
  }, [entries]);

  // Everything in the catalog that fits the open group, before raid / boss /
  // search narrowing — the filter chips are built from this.
  const slotCatalog = useMemo(
    () => (selectedGroup ? catalog.filter((it) => groupAcceptsItem(selectedGroup, it.slot)) : []),
    [catalog, selectedGroup]
  );

  const pickerRaids = useMemo(() => raidsInItems(slotCatalog), [slotCatalog]);

  /** Bosses for the chosen raid only — no raid chosen means no boss row. */
  const pickerBosses = useMemo(
    () =>
      pickerRaid
        ? bossesInItems(slotCatalog.filter((it) => (it.raid_name || UNKNOWN_RAID) === pickerRaid))
        : [],
    [slotCatalog, pickerRaid]
  );

  // Picker: slot-filtered catalog, then sort — on-list BIS first, then upgrade /
  // sidegrade, then items not yet on the list (name within each band).
  const pickerItems = useMemo(() => {
    if (!selectedGroup) return [];
    const q = pickerSearch.trim().toLowerCase();
    const filtered = slotCatalog.filter((it) => {
      if (pickerRaid && (it.raid_name || UNKNOWN_RAID) !== pickerRaid) return false;
      if (pickerBoss && (it.boss_name || "Unknown") !== pickerBoss) return false;
      if (q && !it.name.toLowerCase().includes(q) && !(it.boss_name ?? "").toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
    const listRank = (item: LootItem): number => {
      const ex = entryByItemId.get(item.id);
      if (!ex) return 3;
      if (ex.tag === "bis") return 0;
      if (ex.tag === "upgrade") return 1;
      return 2;
    };
    return [...filtered].sort((a, b) => {
      const r = listRank(a) - listRank(b);
      if (r !== 0) return r;
      return a.name.localeCompare(b.name);
    });
  }, [slotCatalog, selectedGroup, pickerSearch, pickerRaid, pickerBoss, entryByItemId]);

  // ── Mutations ───────────────────────────────────────────────

  const addItem = async (item: LootItem, tag: Tag) => {
    setBusyItemId(item.id);
    try {
      const res = await fetch(`${API_URL}/characters/${character.id}/bis`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ loot_item_id: item.id, tag }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 409 && body.error === "item_missing_slot_data") {
          showToast?.("This item is missing slot data — backfill needed", "error");
        } else if (res.status === 409 || body.error === "item_already_on_list") {
          showToast?.("Already on this character's list", "info");
        } else {
          showToast?.(body.error || `Failed to add (HTTP ${res.status})`, "error");
        }
        return;
      }
      const newRow: BisEntry = await res.json();
      setEntries((prev) => [...prev, newRow]);
      showToast?.(`Added ${item.name} as ${TAG_STYLES[tag].label}`, "success");
    } catch (e: any) {
      showToast?.(e?.message || "Add failed", "error");
    } finally {
      setBusyItemId(null);
    }
  };

  const changeTag = async (entry: BisEntry, nextTag: Tag) => {
    if (entry.tag === nextTag) return;
    setBusyItemId(entry.loot_item_id);
    try {
      const res = await fetch(
        `${API_URL}/characters/${character.id}/bis/${entry.id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tag: nextTag }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        showToast?.(body.error || `Failed to update (HTTP ${res.status})`, "error");
        return;
      }
      setEntries((prev) =>
        prev.map((e) => (e.id === entry.id ? { ...e, tag: nextTag } : e))
      );
    } catch (e: any) {
      showToast?.(e?.message || "Update failed", "error");
    } finally {
      setBusyItemId(null);
    }
  };

  /**
   * Move an entry up or down within its own tag band. Ordering is tag-first
   * (BIS above Upgrade above Sidegrade), so a row can only travel inside its
   * band — to outrank a BIS pick, retag it. Priorities are rewritten as
   * 0..n across the band so later moves stay predictable.
   */
  const moveEntry = async (entry: BisEntry, dir: -1 | 1) => {
    const group = groupsForGame(gameCode).find((g) => groupAcceptsItem(g, entry.slot));
    if (!group) return;
    const band = (entriesByGroup.get(group.id) ?? []).filter((e) => e.tag === entry.tag);
    const from = band.findIndex((e) => e.id === entry.id);
    const to = from + dir;
    if (from < 0 || to < 0 || to >= band.length) return;

    const next = [...band];
    [next[from], next[to]] = [next[to], next[from]];

    // Optimistic — the doll's top pick should move the instant you click.
    const priorityById = new Map(next.map((e, i) => [e.id, i]));
    setEntries((prev) => prev.map((e) => (priorityById.has(e.id) ? { ...e, priority: priorityById.get(e.id)! } : e)));

    setReorderBusy(true);
    try {
      const changed = next.filter((e, i) => e.priority !== i);
      const results = await Promise.all(
        changed.map((e, i) =>
          fetch(`${API_URL}/characters/${character.id}/bis/${e.id}`, {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ priority: priorityById.get(e.id) ?? i }),
          })
        )
      );
      // Without this the optimistic swap stands even when the write was
      // refused, and the row snaps back only on the next open.
      const failed = results.find((r) => !r.ok);
      if (failed) throw new Error(`Could not save that order (HTTP ${failed.status})`);
    } catch (e: any) {
      showToast?.(e?.message || "Could not save that order", "error");
      loadEntries();
    } finally {
      setReorderBusy(false);
    }
  };

  /**
   * The Prio tab's list. A local drag order wins while one exists; anything
   * added since (another tab, an import) falls in at the back rather than
   * vanishing.
   */
  const prioEntries = useMemo(() => {
    const sorted = sortForPrio(entries);
    if (!prioIds) return sorted;
    const byId = new Map(sorted.map((e) => [e.id, e]));
    const out: BisEntry[] = [];
    for (const id of prioIds) {
      const found = byId.get(id);
      if (found) {
        out.push(found);
        byId.delete(id);
      }
    }
    for (const e of sorted) if (byId.has(e.id)) out.push(e);
    return out;
  }, [entries, prioIds]);

  /** One write for the whole list — the ranks are positions, so they all move. */
  const savePrioOrder = async (ordered: BisEntry[]) => {
    const ids = ordered.map((e) => e.id);
    setPrioIds(ids);
    const rankById = new Map(ids.map((id, i) => [id, i + 1]));
    setEntries((prev) => prev.map((e) => ({ ...e, prio_order: rankById.get(e.id) ?? e.prio_order })));

    setPrioBusy(true);
    try {
      const res = await fetch(`${API_URL}/characters/${character.id}/bis/prio-order`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: ids }),
      });
      if (!res.ok) throw new Error(`Could not save that order (HTTP ${res.status})`);
    } catch (e: any) {
      showToast?.(e?.message || "Could not save that order", "error");
      setPrioIds(null);
      loadEntries();
    } finally {
      setPrioBusy(false);
    }
  };

  /** Back to the BIS-list ordering: clear every rank. */
  const resetPrioOrder = async () => {
    setPrioIds(null);
    setEntries((prev) => prev.map((e) => ({ ...e, prio_order: null })));
    setPrioBusy(true);
    try {
      const res = await fetch(`${API_URL}/characters/${character.id}/bis/prio-order`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: [] }),
      });
      if (!res.ok) throw new Error(`Could not reset the order (HTTP ${res.status})`);
    } catch (e: any) {
      showToast?.(e?.message || "Could not reset the order", "error");
      loadEntries();
    } finally {
      setPrioBusy(false);
    }
  };

  const removeEntry = async (entry: BisEntry) => {
    setBusyItemId(entry.loot_item_id);
    try {
      const res = await fetch(
        `${API_URL}/characters/${character.id}/bis/${entry.id}`,
        { method: "DELETE", credentials: "include" }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        showToast?.(body.error || `Failed to remove (HTTP ${res.status})`, "error");
        return;
      }
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      showToast?.(`Removed ${entry.item_name}`, "success");
    } catch (e: any) {
      showToast?.(e?.message || "Remove failed", "error");
    } finally {
      setBusyItemId(null);
    }
  };

  // ── That's My BIS import ────────────────────────────────────

  const closeImport = useCallback(() => {
    setImportOpen(false);
    setImportText("");
    setImportPreview(null);
    setImportError(null);
    setImportExcluded(new Set());
    setImportReplace(false);
  }, []);

  // One endpoint for both passes: preview parses and resolves without writing,
  // commit re-parses the same paste server-side and writes what survived.
  const runImport = async (opts: { commit?: boolean; characterName?: string | null } = {}) => {
    const { commit = false, characterName } = opts;
    setImportBusy(true);
    setImportError(null);
    try {
      const res = await fetch(`${API_URL}/characters/${character.id}/bis/import`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: importText,
          characterName: characterName ?? importPreview?.selected_character ?? undefined,
          commit,
          replace: commit ? importReplace : undefined,
          exclude: commit ? Array.from(importExcluded) : undefined,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(IMPORT_ERRORS[body.error] || body.error || `HTTP ${res.status}`);
      }

      if (!commit) {
        setImportPreview(body as ImportPreview);
        setImportExcluded(new Set());
        return;
      }

      await loadEntries();
      const parts: string[] = [`${body.added} item${body.added === 1 ? "" : "s"} imported`];
      if (body.removed) parts.push(`${body.removed} cleared first`);
      if (body.skipped) parts.push(`${body.skipped} already on your list`);
      showToast?.(parts.join(" · "), body.added > 0 ? "success" : "info");
      closeImport();
    } catch (e: any) {
      setImportError(e?.message || "Import failed");
    } finally {
      setImportBusy(false);
    }
  };

  const toggleImportItem = (externalId: string) => {
    setImportExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(externalId)) next.delete(externalId);
      else next.add(externalId);
      return next;
    });
  };

  // ── Render ──────────────────────────────────────────────────

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-in fade-in"
        onClick={onClose}
      />
      <div className="relative w-full max-w-[1520px] h-[92vh] max-h-[92vh] flex flex-col overflow-hidden rounded-2xl border border-[#2a313c] bg-gradient-to-br from-[#0f1319] via-[#0b0d12] to-[#060708] shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_25px_80px_rgba(0,0,0,0.65),0_0_60px_-20px_rgba(245,158,11,0.12)] animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="relative shrink-0 px-5 py-3.5 border-b border-white/[0.06] bg-gradient-to-r from-black/50 via-transparent to-amber-950/10 flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3.5">
            <span
              className="grid size-11 shrink-0 place-items-center overflow-hidden rounded-xl border-2 bg-black/50"
              style={{ borderColor: `${getWowClassColor(character.class_name)}55` }}
              aria-hidden
            >
              {character.render_url ? (
                <img src={character.render_url} alt="" className="size-full object-cover object-top" />
              ) : (
                <span className="text-lg font-black" style={{ color: getWowClassColor(character.class_name) }}>
                  {character.name.slice(0, 1).toUpperCase()}
                </span>
              )}
            </span>
            <div className="min-w-0">
              <h3 className="truncate text-[20px] font-bold tracking-tight text-white">{character.name}</h3>
              <p className="mt-0.5 truncate text-[12.5px] font-medium text-white/45">
                {[character.spec_label, character.class_name].filter(Boolean).join(" ") || "Character"}
                <span className="text-white/25"> · </span>
                Best-in-slot planner
              </p>
            </div>
          </div>

          {/* The three numbers that answer "where am I at" without reading a single item name. */}
          {!loading && !error && activeTab !== "loot" && (
            <div className="ml-auto hidden shrink-0 items-center gap-5 sm:flex">
              <HeaderStat value={statusTotals.done} label="wearing it" tone="text-emerald-400" />
              <HeaderStat value={statusTotals.want} label="still to get" tone="text-amber-400" />
              <HeaderStat value={statusTotals.none} label="no target" tone="text-red-400" />
            </div>
          )}

          <button
            onClick={onClose}
            className={`shrink-0 rounded-lg border border-transparent p-2 text-white/35 transition-colors hover:border-white/10 hover:bg-white/[0.06] hover:text-white ${
              loading || error || activeTab === "loot" ? "ml-auto" : "sm:ml-0"
            }`}
            aria-label="Close"
          >
            <span className="text-lg leading-none block" aria-hidden>✕</span>
          </button>
        </div>

        {/* Tabs — armory-style pill bar (high-contrast active chip) */}
        <div className="shrink-0 px-5 py-3 border-b border-white/[0.06] bg-black/25 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "bis"}
            onClick={() => {
              setActiveTab("bis");
            }}
            className={`rounded-xl px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider transition-all ${
              activeTab === "bis"
                ? "bg-lime-400 text-neutral-950 border border-lime-300 shadow-[0_0_24px_-10px_rgba(190,244,99,0.75)]"
                : "text-white/42 hover:text-white/75 hover:bg-white/[0.05] border border-transparent rounded-xl"
            }`}
          >
            BIS List
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "prio"}
            onClick={() => {
              setActiveTab("prio");
              setSelectedCellId(null);
            }}
            className={`rounded-xl px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider transition-all flex items-center gap-2 ${
              activeTab === "prio"
                ? "bg-amber-400 text-neutral-950 border border-amber-300 shadow-[0_0_24px_-10px_rgba(251,191,36,0.75)]"
                : "text-white/42 hover:text-white/75 hover:bg-white/[0.05] border border-transparent"
            }`}
            title="Rank your whole list, then export it to Gargul or your tooltip addon"
          >
            Prio
            {entries.length > 0 ? (
              <span
                className={`rounded-md px-1.5 py-0.5 text-[9px] font-bold tabular-nums border ${
                  activeTab === "prio"
                    ? "bg-black/14 text-neutral-900 border-black/18"
                    : "bg-white/[0.08] text-white/70 border-white/15"
                }`}
              >
                {entries.length}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "equipped"}
            onClick={() => {
              setActiveTab("equipped");
              setSelectedCellId(null);
            }}
            className={`rounded-xl px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider transition-all flex items-center gap-2 ${
              activeTab === "equipped"
                ? "bg-lime-400 text-neutral-950 border border-lime-300 shadow-[0_0_24px_-10px_rgba(190,244,99,0.75)]"
                : "text-white/42 hover:text-white/75 hover:bg-white/[0.05] border border-transparent"
            }`}
          >
            Equipped
            {bnetEquipmentItems.length > 0 ? (
              <span
                className={`rounded-md px-1.5 py-0.5 text-[9px] font-bold tabular-nums border ${
                  activeTab === "equipped"
                    ? "bg-black/14 text-neutral-900 border-black/18"
                    : "bg-white/[0.08] text-white/70 border-white/15"
                }`}
              >
                {bnetEquipmentItems.length}
              </span>
            ) : null}
          </button>
          {guildId ? (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "loot"}
              onClick={() => {
                setActiveTab("loot");
                setSelectedCellId(null);
              }}
              className={`rounded-xl px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider transition-all flex items-center gap-2 ${
                activeTab === "loot"
                  ? "bg-violet-500/20 text-violet-50 border border-violet-400/45 shadow-[inset_0_1px_0_rgba(167,139,250,0.12)]"
                  : "text-white/42 hover:text-white/75 hover:bg-white/[0.05] border border-transparent"
              }`}
            >
              Loot history
              {lootAwards.length > 0 ? (
                <span
                  className={`rounded-md px-1.5 py-0.5 text-[9px] font-bold tabular-nums border ${
                    activeTab === "loot"
                      ? "bg-black/22 text-violet-100 border-violet-950/35"
                      : "bg-violet-500/15 text-violet-200/90 border-violet-500/25"
                  }`}
                >
                  {lootAwards.length}
                </span>
              ) : null}
            </button>
          ) : null}
          {canEdit && activeTab === "bis" ? (
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              className="ml-auto shrink-0 inline-flex items-center gap-1.5 rounded-xl border border-lime-400/30 bg-lime-400/[0.08] px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-lime-100/90 transition-colors hover:border-lime-300/50 hover:bg-lime-400/[0.16] hover:text-lime-50"
              title="Import this character's list from That's My BIS"
            >
              <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 3v12" />
                <path d="M7 10l5 5 5-5" />
                <path d="M4 20h16" />
              </svg>
              Import from TMB
            </button>
          ) : null}
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 flex flex-col">
          {activeTab === "loot" ? (
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-[radial-gradient(ellipse_70%_45%_at_50%_0%,rgba(167,139,250,0.07),transparent_55%)] p-4 sm:p-6">
              {lootLoading ? (
                <div className="flex flex-col items-center justify-center gap-3 text-center text-xs text-white/35 py-16">
                  <span className="inline-block size-8 rounded-full border-2 border-white/15 border-t-violet-400/70 animate-spin" aria-hidden />
                  Loading loot history…
                </div>
              ) : lootError ? (
                <div className="text-center text-xs text-red-400/95 py-12 px-4 rounded-xl border border-red-500/20 bg-red-500/5">{lootError}</div>
              ) : lootAwards.length === 0 ? (
                <div className="mx-auto mt-8 max-w-md space-y-3 rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-950/25 via-[#0a0d12] to-black/60 p-8 text-center shadow-[0_0_40px_-16px_rgba(167,139,250,0.4)]">
                  <div className="mx-auto flex size-12 items-center justify-center rounded-xl border border-violet-400/25 bg-black/40 text-violet-300/70">
                    <svg className="size-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M3 7l2-3h14l2 3v13a1 1 0 01-1 1H4a1 1 0 01-1-1V7zm2.5 0h13L17.4 6H6.6L5.5 7zM9 10a3 3 0 006 0h2a5 5 0 01-10 0h2z" />
                    </svg>
                  </div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-violet-300/70">Vault empty</p>
                  <p className="text-xs leading-relaxed text-white/45">
                    Nothing awarded to this character yet. Officers import RCLootCouncil exports from the guild dashboard{" "}
                    <span className="text-white/70">Loot imports</span> tab, and every drop lands here.
                  </p>
                </div>
              ) : (
                <LootHistoryPanel awards={lootAwards} gameCode={gameCode} />
              )}
            </div>
          ) : activeTab === "prio" ? (
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-[radial-gradient(ellipse_70%_45%_at_50%_0%,rgba(245,158,11,0.07),transparent_55%)] p-3 sm:p-5">
              {loading ? (
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-center text-xs text-white/35">
                  <span className="inline-block size-8 animate-spin rounded-full border-2 border-white/15 border-t-amber-500/70" aria-hidden />
                  Loading list…
                </div>
              ) : error ? (
                <div className="mx-auto my-12 max-w-md rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-8 text-center text-xs text-red-400/95">
                  {error}
                </div>
              ) : (
                <PrioPanel
                  entries={prioEntries}
                  gameCode={gameCode}
                  canEdit={canEdit}
                  busy={prioBusy}
                  characterId={character.id}
                  characterName={character.name}
                  guildId={guildId}
                  onReorder={savePrioOrder}
                  onReset={resetPrioOrder}
                  showToast={showToast}
                />
              )}
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto xl:overflow-hidden bg-[radial-gradient(ellipse_75%_45%_at_50%_0%,rgba(245,158,11,0.07),transparent_60%)]">
              {loading ? (
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-center text-xs text-white/35">
                  <span className="inline-block size-8 animate-spin rounded-full border-2 border-white/15 border-t-amber-500/70" aria-hidden />
                  Loading list…
                </div>
              ) : error ? (
                <div className="mx-auto my-12 max-w-md rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-8 text-center text-xs text-red-400/95">
                  {error}
                </div>
              ) : (
                <div
                  data-armory
                  className="grid min-h-0 grid-cols-1 gap-4 p-3 sm:p-4 xl:h-full xl:grid-cols-[290px_minmax(0,1fr)]"
                >
                  {/* LEFT — a work queue, not a second copy of the list */}
                  {activeTab === "bis" ? (
                    <RoadmapRail
                      positions={positions}
                      statusByPos={statusByPos}
                      statusTotals={statusTotals}
                      seatingByGroup={seatingByGroup}
                      gameCode={gameCode}
                      filter={railFilter}
                      onFilter={setRailFilter}
                      selectedCellId={selectedCellId}
                      onSelectCell={setSelectedCellId}
                    />
                  ) : (
                    <EquippedRail
                      equipmentLoaded={equipmentSnapshotLoaded}
                      equipmentItems={bnetEquipmentItems}
                      entriesByGroup={entriesByGroup}
                      gameCode={gameCode}
                      lastUpdatedLabel={equipmentLastUpdatedLabel}
                      profileNamespace={character.bnet_equipment_profile_ns ?? null}
                      breakdown={equipMatch}
                      showAll={showAllEquipped}
                      onShowAll={setShowAllEquipped}
                      canEdit={canEdit}
                      onSelectCell={(id) => {
                        setActiveTab("bis");
                        setSelectedCellId(id);
                      }}
                    />
                  )}

                  {/* CENTRE — paper doll, the whole point of the screen */}
                  <div className="order-first min-w-0 xl:order-none xl:min-h-0 xl:overflow-y-auto xl:overscroll-contain">
                    {activeTab === "equipped" && !equipmentSnapshotLoaded ? (
                      <NoEquipmentCard />
                    ) : (
                      <ArmoryDoll
                        characterName={character.name}
                        classLabel={character.class_name}
                        specLabel={character.spec_label ?? null}
                        renderUrl={character.render_url ?? null}
                        gameCode={gameCode}
                        positions={positions}
                        mode={activeTab === "bis" ? "bis" : "equipped"}
                        resolve={activeTab === "bis" ? resolveBisSlot : resolveEquippedSlot}
                        statusByPos={activeTab === "bis" ? statusByPos : null}
                        selectedCellId={activeTab === "bis" ? selectedCellId : null}
                        canSelect={canEdit}
                        onSelectCell={(id) => {
                          setActiveTab("bis");
                          setSelectedCellId(id);
                        }}
                        headerPill={
                          activeTab === "bis"
                            ? `${bisRoadmapProgress.filled}/${bisRoadmapProgress.total} sockets`
                            : `${bnetEquipmentItems.length} equipped`
                        }
                        footer={
                          activeTab === "bis" ? (
                            <DollLegend
                              canEdit={canEdit}
                              note={
                                emptySocketLabels.length > 0
                                  ? `Still without a target: ${emptySocketLabels.join(", ")}`
                                  : "Every socket has a target. Loot council reads this list."
                              }
                            />
                          ) : (
                            <DollFooter
                              title="Compared to your list"
                              rows={[
                                { label: "Matches a BIS entry", value: equipMatch.bis, dot: TAG_STYLES.bis.dot },
                                { label: "Matches an Upgrade entry", value: equipMatch.upgrade, dot: TAG_STYLES.upgrade.dot },
                                { label: "Not on your list", value: equipMatch.neutral, dot: "bg-white/25" },
                              ]}
                              note={
                                equipmentLastUpdatedLabel
                                  ? `Battle.net snapshot from ${equipmentLastUpdatedLabel}.`
                                  : "Sync from your roster card to refresh this snapshot."
                              }
                            />
                          )
                        }
                      />
                    )}
                  </div>

                </div>
              )}
            </div>
          )}
        </div>

        {/* One slot at a time, on request — targets on the left, loot table on the right. */}
        {selectedGroup && activeTab === "bis" ? (
          <SlotPanel
            group={selectedGroup}
            seating={seatingByGroup.get(selectedGroup.id) ?? null}
            canEdit={canEdit}
            gameCode={gameCode}
            className={character.class_name}
            entries={entriesByGroup.get(selectedGroup.id) ?? []}
            equipped={equippedForGroup(selectedGroup, bnetEquipmentItems)}
            items={pickerItems}
            catalogLoading={catalogLoading}
            totalForSlot={slotCatalog.length}
            raids={pickerRaids}
            bosses={pickerBosses}
            raid={pickerRaid}
            boss={pickerBoss}
            onRaid={(r) => {
              setPickerRaid(r);
              setPickerBoss(null);
            }}
            onBoss={setPickerBoss}
            search={pickerSearch}
            onSearch={setPickerSearch}
            entryByItemId={entryByItemId}
            busyItemId={busyItemId}
            reorderBusy={reorderBusy}
            onAdd={addItem}
            onChangeTag={changeTag}
            onRemove={removeEntry}
            onMove={moveEntry}
            onClose={() => setSelectedCellId(null)}
          />
        ) : null}

        {importOpen && canEdit ? (
          <TmbImportPanel
            characterName={character.name}
            gameCode={gameCode}
            text={importText}
            onTextChange={setImportText}
            busy={importBusy}
            error={importError}
            preview={importPreview}
            replace={importReplace}
            onReplaceChange={setImportReplace}
            excluded={importExcluded}
            currentCount={entries.length}
            onToggleItem={toggleImportItem}
            onPreview={() => runImport()}
            onPickCharacter={(name) => runImport({ characterName: name })}
            onCommit={() => runImport({ commit: true })}
            onBack={() => setImportPreview(null)}
            onClose={closeImport}
          />
        ) : null}
      </div>
    </div>
  );
}

// ── Prio tab ──────────────────────────────────────────────────
//
// The BIS list answers "what do I want for this slot". This answers "if two of
// us want the same drop tonight, who wanted it more", which is the only
// question the in-game addons can ask. One flat list, dragged into order,
// exported as the number that shows up next to your name on the tooltip.
//
// Reordering here never touches the doll: it writes `prio_order`, not
// `priority`.

/** One row of the flat list. Kept dumb so the drag logic stays in the panel. */
function PrioRow({
  entry,
  rank,
  gameCode,
  canEdit,
  dragging,
  canMoveUp,
  canMoveDown,
  onDragStart,
  onDragOver,
  onMove,
}: {
  entry: BisEntry;
  rank: number;
  gameCode: string | null;
  canEdit: boolean;
  dragging: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onDragStart: () => void;
  onDragOver: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const qc = itemQualityHex(bisListRowAccentQualityKey(entry));
  const tag = TAG_STYLES[entry.tag];

  return (
    <li
      onMouseEnter={onDragOver}
      className={`flex items-center gap-2.5 rounded-xl border px-2.5 py-2 transition-colors ${
        dragging
          ? "border-amber-400/60 bg-amber-500/[0.12] shadow-[0_0_24px_-12px_rgba(251,191,36,0.9)]"
          : "border-white/[0.08] bg-black/25 hover:border-white/15"
      }`}
    >
      <span className="w-7 shrink-0 text-right text-[15px] font-black tabular-nums leading-none text-amber-300/85">
        {rank}
      </span>

      {canEdit ? (
        <button
          type="button"
          onMouseDown={onDragStart}
          className="shrink-0 cursor-grab rounded-md px-1 py-1.5 text-white/25 transition-colors hover:bg-white/[0.06] hover:text-white/60 active:cursor-grabbing"
          title="Drag to rank"
          aria-label="Drag to rank"
        >
          <svg className="size-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
            <circle cx="7" cy="5" r="1.4" />
            <circle cx="13" cy="5" r="1.4" />
            <circle cx="7" cy="10" r="1.4" />
            <circle cx="13" cy="10" r="1.4" />
            <circle cx="7" cy="15" r="1.4" />
            <circle cx="13" cy="15" r="1.4" />
          </svg>
        </button>
      ) : null}

      <ItemIcon
        externalItemId={entry.external_item_id}
        gameCode={gameCode}
        name={entry.item_name}
        qualityHex={qc}
        sizeClass="size-9"
      />

      <div className="min-w-0 flex-1">
        <a
          href={wowheadItemUrl(entry.external_item_id, gameCode)}
          target="_blank"
          rel="noopener noreferrer"
          data-wh-rename-link="false"
          className="gear-item-name block truncate text-[13.5px] font-semibold leading-tight hover:underline"
          style={{ color: qc }}
        >
          {entry.item_name}
        </a>
        <p className="mt-0.5 truncate text-[11.5px] text-white/45">
          {slotLabel(entry.slot)}
          <span className="text-white/20"> · </span>
          {sourceLabel(entry)}
        </p>
      </div>

      <span
        className={`hidden shrink-0 rounded border px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wider sm:inline ${tag.pill}`}
      >
        {tag.label}
      </span>

      {canEdit ? (
        <div className="flex shrink-0 flex-col items-center gap-0.5">
          <button
            type="button"
            disabled={!canMoveUp}
            onClick={() => onMove(-1)}
            className="rounded p-0.5 text-white/30 transition-colors hover:bg-white/[0.06] hover:text-white/80 disabled:pointer-events-none disabled:opacity-20"
            aria-label="Move up"
          >
            <svg className="size-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
              <path d="M10 6l5 6H5l5-6z" />
            </svg>
          </button>
          <button
            type="button"
            disabled={!canMoveDown}
            onClick={() => onMove(1)}
            className="rounded p-0.5 text-white/30 transition-colors hover:bg-white/[0.06] hover:text-white/80 disabled:pointer-events-none disabled:opacity-20"
            aria-label="Move down"
          >
            <svg className="size-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
              <path d="M10 14l-5-6h10l-5 6z" />
            </svg>
          </button>
        </div>
      ) : null}
    </li>
  );
}

function PrioPanel({
  entries,
  gameCode,
  canEdit,
  busy,
  characterId,
  characterName,
  guildId,
  onReorder,
  onReset,
  showToast,
}: {
  entries: BisEntry[];
  gameCode: string | null;
  canEdit: boolean;
  busy: boolean;
  characterId: string;
  characterName: string;
  guildId?: string | null;
  onReorder: (ordered: BisEntry[]) => void;
  onReset: () => void;
  showToast?: (message: string, type?: "success" | "error" | "info") => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  /** Live order during a drag. Null means "show what the parent has". */
  const [preview, setPreview] = useState<BisEntry[] | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  const list = preview ?? entries;
  const ranked = entries.some((e) => e.prio_order != null);

  // The gesture ends wherever the mouse comes up, which is often outside the
  // row it started on, so the listener has to be on the window.
  useEffect(() => {
    if (!dragId) return;
    const finish = () => {
      setDragId(null);
      setPreview(null);
      // Read `preview` from the closure rather than from a setState updater:
      // an updater runs during React's render pass, and calling the parent's
      // setState from there is an update-while-rendering. Re-registering the
      // listener on every drag move is the cheaper half of that trade.
      if (preview) onReorder(preview);
    };
    window.addEventListener("mouseup", finish);
    return () => window.removeEventListener("mouseup", finish);
  }, [dragId, preview, onReorder]);

  const moveTo = (id: string, toIndex: number) => {
    const next = [...list];
    const from = next.findIndex((e) => e.id === id);
    if (from === -1 || toIndex < 0 || toIndex >= next.length || from === toIndex) return;
    const [moved] = next.splice(from, 1);
    next.splice(toIndex, 0, moved);
    setPreview(next);
  };

  const moveBy = (entry: BisEntry, dir: -1 | 1) => {
    const from = list.findIndex((e) => e.id === entry.id);
    const to = from + dir;
    if (from === -1 || to < 0 || to >= list.length) return;
    const next = [...list];
    [next[from], next[to]] = [next[to], next[from]];
    setPreview(null);
    onReorder(next);
  };

  if (entries.length === 0) {
    return (
      <div className="mx-auto mt-8 max-w-md space-y-3 rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-950/20 via-[#0a0d12] to-black/60 p-8 text-center shadow-[0_0_40px_-16px_rgba(245,158,11,0.4)]">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-300/70">Nothing to rank yet</p>
        <p className="text-xs leading-relaxed text-white/45">
          Save some targets on the <span className="text-white/70">BIS List</span> tab first. Everything you save shows
          up here, and the order you put it in is the order your raid's addon sees.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="rounded-2xl border border-white/[0.08] bg-black/30 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h4 className="text-[13px] font-bold tracking-tight text-white">Loot priority</h4>
            <p className="mt-1 max-w-lg text-[11.5px] leading-relaxed text-white/45">
              {canEdit
                ? "Drag your whole list into the order you actually want the drops. This is the number that shows next to your name on an item tooltip in game, so 1 is the thing you want most."
                : "The order this character's addon export uses. 1 is the thing they want most."}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canEdit && ranked ? (
              <button
                type="button"
                onClick={onReset}
                disabled={busy}
                className="rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-white/55 transition-colors hover:border-white/25 hover:text-white/85 disabled:opacity-40"
                title="Drop your ranking and go back to the BIS list order"
              >
                Reset
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setExportOpen((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                exportOpen
                  ? "border-amber-300 bg-amber-400 text-neutral-950"
                  : "border-amber-400/30 bg-amber-400/[0.08] text-amber-100/90 hover:border-amber-300/50 hover:bg-amber-400/[0.16]"
              }`}
            >
              <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 21V9" />
                <path d="M7 14l5-5 5 5" />
                <path d="M4 4h16" />
              </svg>
              Export to WoW
            </button>
          </div>
        </div>

        {exportOpen ? (
          <div className="mt-4 border-t border-white/[0.06] pt-4">
            <AddonExportPanel
              endpointBase={`${API_URL}/characters/${characterId}/bis/export`}
              subject={characterName}
              showToast={showToast}
            />
            {guildId ? (
              <p className="mt-3 text-[10.5px] leading-relaxed text-white/30">
                Officers can pull the whole roster in one string from the guild dashboard, under Loot imports.
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <ul
        className={`space-y-1.5 ${dragId ? "select-none" : ""} ${busy ? "opacity-60" : ""}`}
        onMouseLeave={() => {
          // Leaving the list mid-drag would otherwise leave the row stranded
          // wherever the cursor last was.
          if (dragId) setDragId(null);
        }}
      >
        {list.map((entry, index) => (
          <PrioRow
            key={entry.id}
            entry={entry}
            rank={index + 1}
            gameCode={gameCode}
            canEdit={canEdit}
            dragging={dragId === entry.id}
            canMoveUp={index > 0}
            canMoveDown={index < list.length - 1}
            onDragStart={() => setDragId(entry.id)}
            onDragOver={() => {
              if (dragId && dragId !== entry.id) moveTo(dragId, index);
            }}
            onMove={(dir) => moveBy(entry, dir)}
          />
        ))}
      </ul>
    </div>
  );
}

// ── That's My BIS import panel ────────────────────────────────

/** DB slot string -> the paper-doll label the player already knows it by. */
const SLOT_LABEL_BY_DB: Map<string, string> = (() => {
  const m = new Map<string, string>();
  for (const group of groupsForGame(null)) {
    for (const s of group.itemSlots) if (!m.has(s)) m.set(s, group.label);
  }
  return m;
})();

function slotLabel(slot: string | null): string {
  if (!slot) return "Unknown slot";
  return SLOT_LABEL_BY_DB.get(normalizeSlot(slot)) ?? slot.replace(/_/g, " ").toLowerCase();
}

const IMPORT_FORMAT_LABEL: Record<string, string> = {
  gargul: "Gargul export",
  csv: "Wishlist CSV",
  items: "Item links",
};

function ImportStat({ value, label, tone }: { value: number; label: string; tone: string }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-black/40 px-3 py-2">
      <div className={`text-lg font-black tabular-nums leading-none ${tone}`}>{value}</div>
      <div className="mt-1 text-[9px] font-bold uppercase tracking-[0.14em] text-white/35">{label}</div>
    </div>
  );
}

function TmbImportPanel({
  characterName,
  gameCode,
  text,
  onTextChange,
  busy,
  error,
  preview,
  replace,
  onReplaceChange,
  excluded,
  currentCount,
  onToggleItem,
  onPreview,
  onPickCharacter,
  onCommit,
  onBack,
  onClose,
}: {
  characterName: string;
  gameCode: string | null;
  text: string;
  onTextChange: (v: string) => void;
  busy: boolean;
  error: string | null;
  preview: ImportPreview | null;
  replace: boolean;
  onReplaceChange: (v: boolean) => void;
  excluded: Set<string>;
  /** Entries already on the list, so the replace warning can name a number. */
  currentCount: number;
  onToggleItem: (externalId: string) => void;
  onPreview: () => void;
  onPickCharacter: (name: string) => void;
  onCommit: () => void;
  onBack: () => void;
  onClose: () => void;
}) {
  const matched = preview?.matched ?? [];
  const sorted = [...matched].sort(
    (a, b) =>
      slotLabel(a.slot).localeCompare(slotLabel(b.slot)) ||
      a.priority - b.priority ||
      a.item_name.localeCompare(b.item_name)
  );
  const selected = sorted.filter((m) => !excluded.has(m.external_item_id));
  const willAdd = selected.filter((m) => !m.already_on_list).length;

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-[#080a0f]/97 backdrop-blur-sm animate-in fade-in duration-150">
      {/* Panel header */}
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-white/[0.07] bg-gradient-to-r from-lime-950/20 via-transparent to-transparent px-5 py-4">
        <div className="min-w-0">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-lime-400/70">
            Import
          </p>
          <h4 className="truncate text-lg font-bold tracking-tight text-white">
            That's My BIS <span className="text-white/40">to</span> {characterName}
          </h4>
        </div>
        <button
          onClick={onClose}
          className="shrink-0 rounded-lg border border-transparent p-2 text-white/35 transition-colors hover:border-white/10 hover:bg-white/[0.06] hover:text-white"
          aria-label="Close import"
        >
          <span className="block text-lg leading-none" aria-hidden>✕</span>
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
        {!preview ? (
          /* ── Step 1: paste ── */
          <div className="mx-auto max-w-2xl space-y-4">
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-lime-300/70">
                Where to get it
              </p>
              <ol className="mt-2 space-y-1.5 text-xs leading-relaxed text-white/55">
                <li>
                  <span className="text-white/80">1.</span> On thatsmybis.com open your guild, then{" "}
                  <span className="text-white/80">Guild · Export</span>.
                </li>
                <li>
                  <span className="text-white/80">2.</span> Copy the{" "}
                  <span className="text-white/80">Gargul</span> export, or any wishlist CSV.
                </li>
                <li>
                  <span className="text-white/80">3.</span> Paste it below. A guild wide export is fine, we pull out
                  your character.
                </li>
              </ol>
              <p className="mt-3 border-t border-white/[0.06] pt-2.5 text-[11px] leading-relaxed text-white/35">
                No export handy? Item links, Wowhead links or plain item ids (one per line) work too.
              </p>
            </div>

            <textarea
              value={text}
              onChange={(e) => onTextChange(e.target.value)}
              rows={9}
              spellCheck={false}
              placeholder={'Paste your That\'s My BIS export here…'}
              className="w-full resize-y rounded-xl border border-white/10 bg-black/50 px-3 py-2.5 font-mono text-xs text-white/85 placeholder:text-white/25 focus:border-lime-400/50 focus:outline-none"
            />

            {error ? (
              <div className="rounded-xl border border-red-500/25 bg-red-500/[0.07] px-3 py-2.5 text-xs text-red-300/95">
                {error}
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="rounded-xl border border-white/10 px-4 py-2 text-xs font-semibold text-white/55 transition-colors hover:border-white/20 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={onPreview}
                disabled={busy || !text.trim()}
                className="rounded-xl border border-lime-300 bg-lime-400 px-4 py-2 text-xs font-bold uppercase tracking-wider text-neutral-950 transition-colors hover:bg-lime-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {busy ? "Reading…" : "Preview import"}
              </button>
            </div>
          </div>
        ) : preview.needs_character ? (
          /* ── Step 1b: guild wide export, pick whose list this is ── */
          <div className="mx-auto max-w-md space-y-3">
            <p className="text-sm font-semibold text-white">Which character is yours?</p>
            <p className="text-xs leading-relaxed text-white/45">
              This export covers {preview.characters.length} characters and none of them is named {characterName}.
              Pick the one to import onto {characterName}.
            </p>
            <div className="max-h-[46vh] space-y-1.5 overflow-y-auto pr-1">
              {preview.characters.map((c) => (
                <button
                  key={c.name}
                  onClick={() => onPickCharacter(c.name)}
                  disabled={busy}
                  className="flex w-full items-center justify-between gap-3 rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2.5 text-left transition-colors hover:border-lime-400/40 hover:bg-lime-400/[0.06] disabled:opacity-40"
                >
                  <span className="truncate text-sm font-semibold capitalize text-white/85">{c.name}</span>
                  <span className="shrink-0 rounded-md border border-white/10 bg-black/40 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-white/50">
                    {c.count}
                  </span>
                </button>
              ))}
            </div>
            <button
              onClick={onBack}
              className="text-[11px] font-semibold text-white/40 transition-colors hover:text-white/70"
            >
              ← Back to paste
            </button>
          </div>
        ) : (
          /* ── Step 2: review ── */
          <div className="mx-auto max-w-3xl space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <ImportStat value={willAdd} label="To add" tone="text-lime-300" />
              <ImportStat value={preview.counts.duplicates} label="Already listed" tone="text-white/70" />
              <ImportStat value={preview.counts.unmatched} label="Not recognised" tone="text-amber-300/90" />
              <div className="rounded-xl border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-black/40 px-3 py-2">
                <div className="truncate text-sm font-bold capitalize leading-tight text-white/80">
                  {preview.selected_character || characterName}
                </div>
                <div className="mt-1 text-[9px] font-bold uppercase tracking-[0.14em] text-white/35">
                  {IMPORT_FORMAT_LABEL[preview.format] || preview.format}
                </div>
              </div>
            </div>

            {matched.length === 0 ? (
              <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-4 py-6 text-center text-xs leading-relaxed text-amber-100/80">
                Nothing in that export matched this game's loot table. Check you exported the right guild.
              </div>
            ) : (
              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/40">
                    Items found
                  </p>
                  <p className="text-[10px] text-white/30">
                    Untick anything you do not want. Tags come from your TMB ordering.
                  </p>
                </div>
                {sorted.map((m) => {
                  const off = excluded.has(m.external_item_id);
                  return (
                    <div key={m.external_item_id} className={off ? "opacity-40 transition-opacity" : "transition-opacity"}>
                      <GearItemRow
                        externalItemId={m.external_item_id}
                        gameCode={gameCode}
                        name={m.item_name}
                        qualityKey={coerceLootQualityKey(m.item_quality)}
                        onLinkClick={(e) => e.stopPropagation()}
                        secondaryLine={
                          <span className="flex flex-wrap items-center gap-x-1.5">
                            <span className="text-white/60">{slotLabel(m.slot)}</span>
                            {m.boss_name ? <span className="text-white/30">· {m.boss_name}</span> : null}
                            {m.source_type === "prio" ? (
                              <span className="text-amber-300/80">· guild prio</span>
                            ) : null}
                            {m.offspec ? <span className="text-violet-300/80">· offspec</span> : null}
                            {m.already_on_list ? (
                              <span className="text-white/35">· already on your list</span>
                            ) : null}
                          </span>
                        }
                        tagPill={
                          <span
                            className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${TAG_STYLES[m.tag].pill}`}
                          >
                            {TAG_STYLES[m.tag].label}
                          </span>
                        }
                        trailing={
                          <button
                            type="button"
                            onClick={() => onToggleItem(m.external_item_id)}
                            aria-pressed={!off}
                            aria-label={off ? `Include ${m.item_name}` : `Exclude ${m.item_name}`}
                            className={`flex size-5 items-center justify-center rounded-md border transition-colors ${
                              off
                                ? "border-white/15 bg-transparent text-transparent hover:border-white/30"
                                : "border-lime-300 bg-lime-400 text-neutral-950"
                            }`}
                          >
                            <svg className="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <path d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {preview.unmatched.length > 0 ? (
              <details className="rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2.5">
                <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-[0.14em] text-amber-300/70">
                  {preview.unmatched.length} not recognised
                </summary>
                <p className="mt-2 text-[11px] leading-relaxed text-white/40">
                  These are not in Rallify's loot table for this game, usually crafted gear, world drops or items from a
                  raid that is not loaded yet. Add them by hand if you need them.
                </p>
                <ul className="mt-2 space-y-1">
                  {preview.unmatched.map((u) => (
                    <li key={u.external_item_id} className="flex items-center gap-2 text-[11px] text-white/45">
                      <a
                        href={wowheadItemUrl(u.external_item_id, gameCode)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-wh-rename-link="false"
                        className="text-sky-300/80 hover:underline"
                      >
                        {u.item_name || `Item ${u.external_item_id}`}
                      </a>
                      <span className="text-white/25">
                        {u.reason === "missing_slot_data" ? "no slot data" : "not in loot table"}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}

            {error ? (
              <div className="rounded-xl border border-red-500/25 bg-red-500/[0.07] px-3 py-2.5 text-xs text-red-300/95">
                {error}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Footer actions, review step only */}
      {preview && !preview.needs_character ? (
        <div className="shrink-0 border-t border-white/[0.07] bg-black/40 px-5 py-3">
          <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-1 rounded-xl border border-white/10 bg-black/40 p-1">
              {[
                { key: false, label: "Add to list" },
                { key: true, label: "Replace list" },
              ].map((opt) => (
                <button
                  key={String(opt.key)}
                  onClick={() => onReplaceChange(opt.key)}
                  className={`rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                    replace === opt.key
                      ? "bg-white/[0.12] text-white"
                      : "text-white/40 hover:text-white/70"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={onBack}
                disabled={busy}
                className="rounded-xl border border-white/10 px-4 py-2 text-xs font-semibold text-white/55 transition-colors hover:border-white/20 hover:text-white disabled:opacity-40"
              >
                Back
              </button>
              <button
                onClick={onCommit}
                disabled={busy || selected.length === 0}
                className="rounded-xl border border-lime-300 bg-lime-400 px-4 py-2 text-xs font-bold uppercase tracking-wider text-neutral-950 transition-colors hover:bg-lime-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {busy ? "Importing…" : replace ? `Replace with ${selected.length}` : `Import ${willAdd}`}
              </button>
            </div>
          </div>
          {replace && currentCount > 0 ? (
            <p className="mx-auto mt-2 max-w-3xl text-[11px] text-amber-300/70">
              Clears all {currentCount} current entries on {characterName} first.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────

function GearItemRow({
  externalItemId,
  gameCode,
  name,
  qualityKey,
  tagPill,
  secondaryLine,
  trailing,
  onLinkClick,
}: {
  externalItemId: string | number;
  gameCode: string | null;
  name: string;
  /** Normalized quality token (e.g. "4", "epic"); see `coerceLootQualityKey`. */
  qualityKey: string;
  /** Right-aligned tag chip (BIS / Upgrade / Sidegrade, or equipped-match level). */
  tagPill?: React.ReactNode;
  /** Small sub-line under the name (boss name, "On your list as BIS", etc). */
  secondaryLine?: React.ReactNode;
  /** Anything to render at the far right after the tag pill (extra controls). */
  trailing?: React.ReactNode;
  onLinkClick?: (e: React.MouseEvent) => void;
}) {
  const qc = itemQualityHex(qualityKey);
  const href = wowheadItemUrl(externalItemId, gameCode);

  return (
    <div
      className="group/gear flex items-center gap-2.5 rounded-lg border border-white/[0.08] bg-[#07080d]/95 px-2 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] transition-colors hover:border-white/[0.16] hover:bg-[#0b0d14] sm:gap-3 sm:px-2.5 sm:py-2"
      style={{ borderLeftWidth: 3, borderLeftColor: qc }}
    >
      {/*
        Icon anchor — Wowhead injects the item tile here (`data-wh-icon-size="medium"`).
        The name link keeps the tooltip but uses `gear-item-name` CSS to suppress
        Wowhead's duplicate small icon (`iconizeLinks` is on globally).
      */}
      
        <a href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onLinkClick}
        aria-label={name}
        title={name}
        data-wh-icon-size="medium"
        data-wh-rename-link="false"
        className="gear-icon-tile relative inline-flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/10 bg-black/55 text-[9px] font-bold uppercase tracking-wider text-white/25 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] sm:size-10"
      >
        {/* Placeholder shown only while Wowhead's icon background-image has not loaded yet. */}
        <span aria-hidden className="pointer-events-none select-none">·</span>
      </a>
      <div className="min-w-0 flex-1">
        
         <a href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onLinkClick}
          data-wh-rename-link="false"
          className="gear-item-name block min-w-0 truncate text-[12px] font-semibold leading-snug tracking-tight underline-offset-2 decoration-from-font decoration-current/35 hover:underline hover:decoration-current/55 sm:text-[13px]"
          style={{ color: qc }}
        >
          {name}
        </a>
        {secondaryLine ? (
          <div className="mt-0.5 min-w-0 truncate text-[10px] font-medium leading-snug text-white/55 sm:text-[11px]">
            {secondaryLine}
          </div>
        ) : null}
      </div>
      {tagPill ? <div className="shrink-0">{tagPill}</div> : null}
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </div>
  );
}

function BisMatchPill({ level }: { level: "bis" | "upgrade" | "neutral" }) {
  if (level === "bis") {
    return (
      <span
        className="shrink-0 inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/12 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-100/95 shadow-[inset_0_1px_0_rgba(251,191,36,0.12)]"
        title="This item is on your list as BIS"
      >
        <span className="size-1.5 rounded-full bg-amber-400 shadow-sm" aria-hidden />
        BIS
      </span>
    );
  }
  if (level === "upgrade") {
    return (
      <span
        className="shrink-0 inline-flex items-center gap-1 rounded-md border border-emerald-500/35 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-100/95 shadow-[inset_0_1px_0_rgba(52,211,153,0.1)]"
        title="This item is on your list as Upgrade"
      >
        <span className="size-1.5 rounded-full bg-emerald-400 shadow-sm" aria-hidden />
        Up
      </span>
    );
  }
  return (
    <span
      className="shrink-0 inline-flex items-center rounded-md border border-white/[0.08] bg-white/[0.03] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white/32"
      title="Not flagged as BIS or Upgrade on your list for this slot"
    >
      —
    </span>
  );
}

function equipmentMatchBreakdown(
  gameCode: string | null,
  equipmentItems: EquippedPiece[],
  entriesByGroup: Map<string, BisEntry[]>
): { bis: number; upgrade: number; neutral: number; total: number } {
  let bis = 0;
  let upgrade = 0;
  let neutral = 0;
  const counted = new Set<string>();
  for (const group of groupsForGame(gameCode)) {
    for (const p of equippedForGroup(group, equipmentItems)) {
      const key = `${p.slot_type}-${p.item_id}`;
      if (counted.has(key)) continue;
      counted.add(key);
      const lvl = matchEquippedToBis(p.item_id, entriesByGroup.get(group.id) ?? []);
      if (lvl === "bis") bis++;
      else if (lvl === "upgrade") upgrade++;
      else neutral++;
    }
  }
  return { bis, upgrade, neutral, total: bis + upgrade + neutral };
}

/** Shown in the centre column when Battle.net has never returned equipment. */
function NoEquipmentCard() {
  return (
    <div className="mx-auto mt-6 max-w-lg space-y-4 rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-950/25 via-[#0a0d12] to-black/60 p-8 text-center shadow-[0_0_40px_-14px_rgba(245,158,11,0.35)]">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400/80">No equipment snapshot</div>
      <p className="text-sm leading-relaxed text-white/55">
        Use the <span className="font-semibold text-amber-200/90">Sync</span> button on your roster card to pull your Battle.net{" "}
        <span className="text-white/75">portrait</span>, <span className="text-white/75">equipped gear</span>, and{" "}
        <span className="text-white/75">Warcraft Logs</span> parse in one go.
      </p>
      <p className="text-[11px] leading-relaxed text-white/35">
        Requires Battle.net API credentials on the server and a public WoW profile.
      </p>
    </div>
  );
}

/** Set-bonus style strip under the doll — counts plus one line of context. */
function HeaderStat({ value, label, tone }: { value: number; label: string; tone: string }) {
  return (
    <div className="text-right">
      <div className={`text-[22px] font-black leading-none tabular-nums ${tone}`}>{value}</div>
      <div className="mt-1 text-[11px] font-medium text-white/40">{label}</div>
    </div>
  );
}

/** Replaces the old tag tally: the doll's colours are what need explaining. */
function DollLegend({ canEdit, note }: { canEdit: boolean; note: string }) {
  return (
    <div className="mt-3 rounded-xl border border-white/[0.07] bg-black/25 px-3.5 py-3">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        {(["done", "want", "none"] as SlotStatus[]).map((s) => (
          <span key={s} className="inline-flex items-center gap-2 text-[12px] font-medium text-white/55">
            <span className={`size-2.5 rounded-full ${SLOT_STATUS_META[s].dot}`} aria-hidden />
            {SLOT_STATUS_META[s].label}
          </span>
        ))}
      </div>
      <p className="mt-2 text-[12px] leading-snug text-white/35">
        {note}
        {canEdit ? <span className="text-white/25"> · Click any slot to see its other options.</span> : null}
      </p>
    </div>
  );
}

function DollFooter({
  title,
  rows,
  note,
}: {
  title: string;
  rows: { label: string; value: number; dot: string }[];
  note?: string | null;
}) {
  return (
    <div className="mt-3 rounded-xl border border-white/[0.07] bg-black/35 px-3 py-2.5">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-500/70">{title}</p>
      <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-1">
        {rows.map((r) => (
          <span key={r.label} className="inline-flex items-center gap-1.5 text-[11px] text-white/50">
            <span className={`size-1.5 shrink-0 rounded-full ${r.dot}`} aria-hidden />
            {r.label}
            <span className="font-bold tabular-nums text-white/85">{r.value}</span>
          </span>
        ))}
      </div>
      {note ? <p className="mt-1.5 text-[10px] leading-snug text-white/32">{note}</p> : null}
    </div>
  );
}

/** Wowhead-backed icon square: the anchor carries the tooltip and the injected icon art. */
function ItemIcon({
  externalItemId,
  gameCode,
  name,
  qualityHex,
  sizeClass,
  emptyLabel,
  emptyPlus,
  iconSize = "medium",
}: {
  externalItemId?: string | number | null;
  gameCode: string | null;
  name?: string;
  qualityHex?: string | null;
  sizeClass: string;
  /** Wowhead art size — "large" (56px) for hero tiles, "medium" (36px) elsewhere. */
  iconSize?: "medium" | "large";
  /** Two-letter hint drawn in empty sockets (e.g. "HE" for Head). */
  emptyLabel?: string;
  /** Empty socket the player can fill — draw a plus instead of the letters. */
  emptyPlus?: boolean;
}) {
  if (externalItemId == null) {
    return (
      <span
        className={`inline-flex shrink-0 items-center justify-center rounded-md border border-dashed shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] ${
          emptyPlus
            ? "border-amber-500/40 bg-amber-500/[0.07] text-amber-300/80"
            : "border-white/12 bg-black/50 text-[8px] font-bold uppercase tracking-wider text-white/20"
        } ${sizeClass}`}
        aria-hidden
      >
        {emptyPlus ? (
          <svg className="size-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 3.75a.75.75 0 01.75.75v4.75h4.75a.75.75 0 010 1.5H10.75v4.75a.75.75 0 01-1.5 0V10.75H4.5a.75.75 0 010-1.5h4.75V4.5a.75.75 0 01.75-.75z" />
          </svg>
        ) : (
          emptyLabel ?? ""
        )}
      </span>
    );
  }
  return (
    <a
      href={wowheadItemUrl(externalItemId, gameCode)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      data-wh-icon-size={iconSize}
      data-wh-rename-link="false"
      aria-label={name}
      title={name}
      className={`gear-icon-tile inline-flex shrink-0 items-center justify-center overflow-hidden rounded-md border-2 bg-black/60 text-[9px] font-bold text-white/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] ${sizeClass}`}
      style={{ borderColor: qualityHex ?? "#2a3038" }}
    >
      <span aria-hidden className="pointer-events-none select-none">·</span>
    </a>
  );
}

/** One paper-doll socket: icon plus the item name, mirrored on the right-hand column. */
function DollTile({
  pos,
  view,
  side,
  gameCode,
  status,
  selected,
  clickable,
  onSelect,
}: {
  pos: DollPos;
  view: DollSlotView;
  side: "left" | "right" | "bottom";
  gameCode: string | null;
  /** BIS mode only — paints the socket by how far along the slot is. */
  status: SlotStatus | null;
  selected: boolean;
  clickable: boolean;
  onSelect: (cellId: string) => void;
}) {
  const item = view.item;
  const qc = item ? itemQualityHex(item.qualityKey) : null;
  const isBottom = side === "bottom";
  const meta = status ? SLOT_STATUS_META[status] : null;

  const icon = (
    <span className="relative inline-flex shrink-0">
      <ItemIcon
        externalItemId={item?.externalItemId ?? null}
        gameCode={gameCode}
        name={item?.name}
        qualityHex={qc}
        sizeClass={isBottom ? "size-12 sm:size-14" : "size-11 sm:size-[52px]"}
        iconSize={isBottom ? "large" : "medium"}
        emptyLabel={pos.label.slice(0, 2)}
        emptyPlus={clickable && !item && !!status}
      />
      {/* Status dot sits on the socket itself — scan the doll, not the labels. */}
      {meta && !pos.cosmetic && status !== "na" ? (
        <span
          className="pointer-events-none absolute -left-1 -top-1 size-3 rounded-full border-2 border-[#0c0f14]"
          style={{ background: meta.hex }}
          title={meta.label}
          aria-hidden
        />
      ) : null}
      {view.extra > 0 ? (
        <span
          className="pointer-events-none absolute -bottom-1.5 -right-1.5 rounded-md border border-white/15 bg-[#0f1319] px-1.5 py-px text-[10px] font-bold tabular-nums text-white/70"
          title={`${view.extra} more option${view.extra === 1 ? "" : "s"} saved for this slot`}
        >
          +{view.extra}
        </span>
      ) : null}
    </span>
  );

  const text = (
    // Narrow viewports stack the doll into one column, so the right-hand
    // sockets only mirror from `sm` up.
    <span
      className={`block min-w-0 ${
        // Bottom tiles stack vertically, so flex-1 would size height, not width.
        isBottom ? "w-full text-center" : "flex-1"
      } ${side === "right" ? "text-left sm:text-right" : isBottom ? "" : "text-left"}`}
    >
      <span className="block truncate text-[10px] font-bold uppercase tracking-[0.12em] text-white/42">
        {pos.label}
      </span>
      {item ? (
        <>
          <span className="mt-0.5 block truncate text-[13px] font-semibold leading-tight" style={{ color: qc ?? undefined }}>
            {item.name}
          </span>
          {/* Only worth saying when the top pick isn't an actual BIS target. */}
          {item.note ? (
            <span className={`mt-0.5 block truncate text-[10px] font-bold uppercase tracking-wider ${item.noteClass ?? "text-white/35"}`}>
              {item.note}
            </span>
          ) : null}
        </>
      ) : (
        <span
          className={`mt-0.5 block truncate text-[12.5px] font-semibold leading-tight ${
            view.coveredBy
              ? "text-white/40 italic"
              : clickable && status && status !== "na"
                ? "text-amber-300/70 transition-colors group-hover/tile:text-amber-200"
                : "text-white/25"
          }`}
          title={view.coveredBy ?? undefined}
        >
          {/* A two-hander occupies this socket, so there is nothing to plan here. */}
          {view.coveredBy
            ? `Taken by ${view.coveredBy}`
            : pos.cosmetic
              ? "Nothing set"
              : !status
                ? "Nothing equipped"
                : clickable
                  ? "Set a target"
                  : "Nothing set"}
        </span>
      )}
    </span>
  );

  const shell = `group/tile flex items-center gap-2.5 rounded-xl border px-2 py-1.5 transition-colors ${
    isBottom
      ? "w-[142px] flex-col gap-2 px-2 py-2"
      : side === "right"
        ? "w-full flex-row sm:flex-row-reverse"
        : "w-full"
  } ${
    selected
      ? "border-amber-500/55 bg-amber-500/[0.10]"
      : clickable
        ? "border-transparent hover:border-amber-500/30 hover:bg-amber-500/[0.06]"
        : "border-transparent"
  }`;

  // Cosmetic sockets and anything a two-hander already covers are inert.
  if (!clickable || pos.cosmetic || view.coveredBy) {
    return (
      <div className={`${shell} ${pos.cosmetic || view.coveredBy ? "opacity-45" : ""}`} title={pos.label}>
        {icon}
        {text}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(pos.group.id)}
      title={`${pos.label}${meta ? ` — ${meta.label.toLowerCase()}` : ""}. Click to see every option.`}
      className={`${shell} text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/45`}
    >
      {icon}
      {text}
    </button>
  );
}

/** Centre column: the character sheet itself — 8 sockets a side, weapons beneath. */
function ArmoryDoll({
  characterName,
  classLabel,
  specLabel,
  renderUrl,
  gameCode,
  positions,
  mode,
  resolve,
  statusByPos,
  selectedCellId,
  canSelect,
  onSelectCell,
  headerPill,
  footer,
}: {
  characterName: string;
  classLabel: string | null;
  specLabel: string | null;
  renderUrl: string | null;
  gameCode: string | null;
  positions: DollPos[];
  mode: "bis" | "equipped";
  resolve: (pos: DollPos) => DollSlotView;
  /** Null in equipped mode — status is a property of your BIS plan. */
  statusByPos: Map<string, SlotStatus> | null;
  selectedCellId: string | null;
  canSelect: boolean;
  onSelectCell: (cellId: string) => void;
  headerPill: string;
  footer?: React.ReactNode;
}) {
  const classColor = getWowClassColor(classLabel);

  const portrait = (
    <div className="relative flex w-full items-center justify-center py-2">
      <div
        className="pointer-events-none absolute inset-0 blur-2xl"
        style={{ background: `radial-gradient(circle at 50% 45%, ${classColor}1f, transparent 62%)` }}
        aria-hidden
      />
      <div
        className="relative flex size-24 items-center justify-center overflow-hidden rounded-full border-2 bg-black/55 shadow-[0_8px_24px_rgba(0,0,0,0.55)] lg:size-32"
        style={{ borderColor: `${classColor}66` }}
      >
        {renderUrl ? (
          <img src={renderUrl} alt="" className="size-full object-cover object-top" />
        ) : (
          <span className="text-4xl font-black" style={{ color: classColor }}>
            {characterName.slice(0, 1).toUpperCase()}
          </span>
        )}
      </div>
    </div>
  );

  const renderTile = (pos: DollPos, side: "left" | "right" | "bottom") => (
    <DollTile
      key={pos.key}
      pos={pos}
      view={resolve(pos)}
      side={side}
      gameCode={gameCode}
      status={statusByPos?.get(pos.key) ?? null}
      selected={selectedCellId === pos.group.id}
      clickable={canSelect && !pos.cosmetic}
      onSelect={onSelectCell}
    />
  );

  const column = (col: "left" | "right" | "bottom") => positions.filter((p) => p.group.column === col);

  return (
    <div className="rounded-2xl border border-[#2c3340] bg-[linear-gradient(165deg,rgba(255,255,255,0.04)_0%,rgba(0,0,0,0.22)_55%,rgba(0,0,0,0.4)_100%)] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_12px_40px_-24px_rgba(0,0,0,0.85)] sm:p-4">
      {/* Header — name, spec, and what this doll is showing */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/[0.07] pb-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="size-2.5 shrink-0 rounded-full"
            style={{ background: classColor, boxShadow: `0 0 12px ${classColor}66` }}
            aria-hidden
          />
          <div className="min-w-0">
            <div className="truncate text-[16px] font-bold leading-tight tracking-tight" style={{ color: classColor }}>
              {characterName}
            </div>
            <div className="mt-0.5 truncate text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35">
              {[specLabel, classLabel].filter(Boolean).join(" ") || "Character"}
              <span className="text-white/20"> · </span>
              {mode === "bis" ? "BIS targets" : "Equipped"}
            </div>
          </div>
        </div>
        <span className="shrink-0 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider tabular-nums text-amber-100/90">
          {headerPill}
        </span>
      </div>

      {/* Portrait floats above the sockets on narrow viewports */}
      <div className="mt-3 sm:hidden">{portrait}</div>

      <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:gap-4">
        <div className="space-y-1.5">{column("left").map((p) => renderTile(p, "left"))}</div>
        <div className="hidden w-[140px] items-center justify-center sm:flex lg:w-[210px]">{portrait}</div>
        <div className="space-y-1.5">{column("right").map((p) => renderTile(p, "right"))}</div>
      </div>

      <div className="mt-3 flex flex-wrap items-start justify-center gap-3 border-t border-white/[0.06] pt-3">
        {column("bottom").map((p) => renderTile(p, "bottom"))}
      </div>

      {footer}
    </div>
  );
}

/** One line in the left rail: status, slot, and the single item that slot is about. */
function RailSlotRow({
  pos,
  status,
  seating,
  gameCode,
  selected,
  onSelect,
}: {
  pos: DollPos;
  status: SlotStatus;
  seating: Seating<BisEntry> | null;
  gameCode: string | null;
  selected: boolean;
  onSelect: (cellId: string) => void;
}) {
  const entry = seating?.seats[pos.index] ?? null;
  const qc = entry ? itemQualityHex(bisListRowAccentQualityKey(entry)) : null;
  const meta = SLOT_STATUS_META[status];
  // Alternates belong to the group, so only its last socket reports them.
  const hidden = pos.index === pos.group.sockets.length - 1 ? seating?.extra.length ?? 0 : 0;

  return (
    <button
      type="button"
      onClick={() => onSelect(pos.group.id)}
      className={`flex w-full items-center gap-2.5 rounded-xl border px-2 py-1.5 text-left transition-colors ${
        selected
          ? "border-amber-500/45 bg-amber-500/[0.09]"
          : "border-transparent hover:border-white/10 hover:bg-white/[0.04]"
      }`}
      style={{ borderLeft: `3px solid ${meta.hex}` }}
      title={`${pos.label} — ${meta.label.toLowerCase()}`}
    >
      <ItemIcon
        externalItemId={entry?.external_item_id ?? null}
        gameCode={gameCode}
        name={entry?.item_name}
        qualityHex={qc}
        sizeClass="size-8"
        emptyPlus
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[10px] font-bold uppercase tracking-[0.12em] text-white/45">
          {pos.label}
        </span>
        <span
          className="mt-px block truncate text-[12.5px] font-semibold leading-tight"
          style={{ color: entry ? qc ?? undefined : undefined }}
        >
          {entry ? (
            entry.item_name
          ) : status === "na" ? (
            <span className="italic text-white/35">Not applicable</span>
          ) : (
            <span className="text-amber-300/70">Set a target</span>
          )}
        </span>
      </span>
      {hidden > 0 ? (
        <span className="shrink-0 rounded-md border border-white/12 bg-white/[0.05] px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-white/50">
          +{hidden}
        </span>
      ) : null}
    </button>
  );
}

/**
 * Left column. Not a second copy of the list — a work queue. It shows one
 * line per paper-doll socket and nothing more; the filter chips turn it into
 * "what still needs a target" or "what I'm still chasing".
 */
function RoadmapRail({
  positions,
  statusByPos,
  statusTotals,
  seatingByGroup,
  gameCode,
  filter,
  onFilter,
  selectedCellId,
  onSelectCell,
}: {
  positions: DollPos[];
  statusByPos: Map<string, SlotStatus>;
  statusTotals: Record<SlotStatus, number>;
  seatingByGroup: Map<string, Seating<BisEntry>>;
  gameCode: string | null;
  filter: SlotStatus | "all";
  onFilter: (f: SlotStatus | "all") => void;
  selectedCellId: string | null;
  onSelectCell: (cellId: string) => void;
}) {
  // Sockets nothing can ever be planned into stay out of the queue entirely.
  const plannable = useMemo(
    () => positions.filter((p) => !p.cosmetic && statusByPos.get(p.key) !== "na"),
    [positions, statusByPos]
  );
  const visible = plannable.filter((p) => filter === "all" || statusByPos.get(p.key) === filter);
  const total = plannable.length;

  const chips: { key: SlotStatus | "all"; label: string; count: number; tone: string }[] = [
    { key: "all", label: "All slots", count: total, tone: "" },
    { key: "none", label: "No target", count: statusTotals.none, tone: "text-red-300" },
    { key: "want", label: "To get", count: statusTotals.want, tone: "text-amber-300" },
    { key: "done", label: "Done", count: statusTotals.done, tone: "text-emerald-300" },
  ];

  return (
    <div className="order-2 flex min-w-0 flex-col overflow-hidden rounded-2xl border border-[#2c3340] bg-[linear-gradient(180deg,rgba(255,255,255,0.05)_0%,rgba(0,0,0,0.32)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_12px_40px_-24px_rgba(0,0,0,0.85)] xl:order-none xl:min-h-0">
      <div className="shrink-0 space-y-2.5 border-b border-white/[0.07] bg-black/25 px-3 py-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[14px] font-bold tracking-tight text-white">Your slots</span>
          <span className="rounded-md border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-white/55">
            {visible.length}
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {chips.map((c) => {
            const on = filter === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => onFilter(c.key)}
                disabled={c.count === 0 && c.key !== "all"}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-[11.5px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${
                  on
                    ? "border-lime-300/70 bg-lime-400 text-neutral-950"
                    : "border-white/10 bg-white/[0.03] text-white/55 hover:border-white/20 hover:bg-white/[0.07] hover:text-white"
                }`}
              >
                {c.label}
                <span
                  className={`rounded px-1 text-[10.5px] font-bold tabular-nums ${
                    on ? "bg-black/20 text-neutral-900" : `bg-white/[0.07] ${c.tone || "text-white/50"}`
                  }`}
                >
                  {c.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto overscroll-contain p-2">
        {visible.length === 0 ? (
          <p className="px-3 py-10 text-center text-[12.5px] leading-relaxed text-white/35">
            {filter === "none"
              ? "Every socket has a target. Nice."
              : filter === "want"
                ? "Nothing outstanding — you're wearing every target you set."
                : "Nothing here yet."}
          </p>
        ) : (
          visible.map((pos) => (
            <RailSlotRow
              key={pos.key}
              pos={pos}
              status={statusByPos.get(pos.key) ?? "none"}
              seating={seatingByGroup.get(pos.group.id) ?? null}
              gameCode={gameCode}
              selected={selectedCellId === pos.group.id}
              onSelect={onSelectCell}
            />
          ))
        )}
      </div>
    </div>
  );
}

/**
 * Equipped tab's left column. The doll already shows what's worn, so this
 * answers the only other question: what are you wearing that your list
 * never accounted for?
 */
function EquippedRail({
  equipmentLoaded,
  equipmentItems,
  entriesByGroup,
  gameCode,
  lastUpdatedLabel,
  profileNamespace,
  breakdown,
  showAll,
  onShowAll,
  canEdit,
  onSelectCell,
}: {
  equipmentLoaded: boolean;
  equipmentItems: EquippedPiece[];
  entriesByGroup: Map<string, BisEntry[]>;
  gameCode: string | null;
  lastUpdatedLabel: string | null;
  profileNamespace: string | null;
  breakdown: { bis: number; upgrade: number; neutral: number };
  showAll: boolean;
  onShowAll: (v: boolean) => void;
  canEdit: boolean;
  onSelectCell: (cellId: string) => void;
}) {
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const out: { piece: EquippedPiece; group: GearGroup; level: "bis" | "upgrade" | "neutral" }[] = [];
    for (const group of planningGroups(gameCode)) {
      for (const piece of equippedForGroup(group, equipmentItems)) {
        const key = `${piece.slot_type}-${piece.item_id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
          piece,
          group,
          level: matchEquippedToBis(piece.item_id, entriesByGroup.get(group.id) ?? []),
        });
      }
    }
    return out;
  }, [equipmentItems, entriesByGroup, gameCode]);

  const offList = rows.filter((r) => r.level === "neutral");
  const shown = showAll ? rows : offList;

  return (
    <div className="order-2 flex min-w-0 flex-col overflow-hidden rounded-2xl border border-[#2c3340] bg-[linear-gradient(180deg,rgba(255,255,255,0.05)_0%,rgba(0,0,0,0.32)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_12px_40px_-24px_rgba(0,0,0,0.85)] xl:order-none xl:min-h-0">
      <div className="shrink-0 space-y-2.5 border-b border-white/[0.07] bg-black/25 px-3 py-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[14px] font-bold tracking-tight text-white">
            {showAll ? "Everything equipped" : "Not on your list"}
          </span>
          <span className="rounded-md border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-white/55">
            {shown.length}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[11.5px] font-medium text-white/45">
          <span className="inline-flex items-center gap-1.5">
            <span className={`size-2 rounded-full ${TAG_STYLES.bis.dot}`} aria-hidden />
            {breakdown.bis} BIS
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className={`size-2 rounded-full ${TAG_STYLES.upgrade.dot}`} aria-hidden />
            {breakdown.upgrade} upgrade
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-white/25" aria-hidden />
            {breakdown.neutral} off-list
          </span>
        </div>
        <button
          type="button"
          onClick={() => onShowAll(!showAll)}
          className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-[11.5px] font-semibold text-white/55 transition-colors hover:border-white/20 hover:bg-white/[0.07] hover:text-white"
        >
          {showAll ? "Show only what's missing from my list" : `Show all ${rows.length} equipped`}
        </button>
        <p className="truncate text-[11px] text-white/30">
          {lastUpdatedLabel ? `Synced ${lastUpdatedLabel}` : "Not synced yet"}
          {profileNamespace ? <span className="text-white/20"> · {profileNamespace}</span> : null}
        </p>
      </div>

      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto overscroll-contain p-2">
        {!equipmentLoaded || rows.length === 0 ? (
          <p className="px-2 py-8 text-center text-[12.5px] leading-relaxed text-white/30">
            Nothing reported by Battle.net yet. Hit <span className="text-amber-200/80">Sync</span> on your roster card
            to pull equipped gear.
          </p>
        ) : shown.length === 0 ? (
          <p className="px-2 py-8 text-center text-[12.5px] leading-relaxed text-white/35">
            Every piece you're wearing is accounted for on your list.
          </p>
        ) : (
          shown.map(({ piece, group, level }) => (
            <GearItemRow
              key={`${piece.slot_type}-${piece.item_id}`}
              externalItemId={piece.item_id}
              gameCode={gameCode}
              name={piece.name}
              qualityKey={coerceLootQualityKey(piece.quality)}
              secondaryLine={<span className="uppercase tracking-wider text-white/35">{group.label}</span>}
              tagPill={
                level === "neutral" ? (
                  canEdit ? (
                    <button
                      type="button"
                      onClick={() => onSelectCell(group.id)}
                      className="shrink-0 rounded-lg border border-amber-500/35 bg-amber-500/10 px-2 py-1 text-[10.5px] font-bold uppercase tracking-wider text-amber-200/90 transition-colors hover:border-amber-400/70 hover:bg-amber-500/20 hover:text-amber-50"
                      title={`Open the ${group.label} slot`}
                    >
                      Plan slot
                    </button>
                  ) : undefined
                ) : (
                  <BisMatchPill level={level} />
                )
              }
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── Slot panel ────────────────────────────────────────────────
//
// Everything about one slot, opened on request. Left half is what the
// player already saved (ordered — the top row is what the doll shows);
// right half is the loot table narrowed by raid, then boss.

function SlotPanel({
  group,
  seating,
  canEdit,
  gameCode,
  className,
  entries,
  equipped,
  items,
  catalogLoading,
  totalForSlot,
  raids,
  bosses,
  raid,
  boss,
  onRaid,
  onBoss,
  search,
  onSearch,
  entryByItemId,
  busyItemId,
  reorderBusy,
  onAdd,
  onChangeTag,
  onRemove,
  onMove,
  onClose,
}: {
  group: GearGroup;
  seating: Seating<BisEntry> | null;
  canEdit: boolean;
  gameCode: string | null;
  className: string | null;
  entries: BisEntry[];
  equipped: EquippedPiece[];
  items: LootItem[];
  catalogLoading: boolean;
  totalForSlot: number;
  raids: string[];
  bosses: string[];
  raid: string | null;
  boss: string | null;
  onRaid: (r: string | null) => void;
  onBoss: (b: string | null) => void;
  search: string;
  onSearch: (s: string) => void;
  entryByItemId: Map<number, BisEntry>;
  busyItemId: number | null;
  reorderBusy: boolean;
  onAdd: (item: LootItem, tag: Tag) => void;
  onChangeTag: (entry: BisEntry, tag: Tag) => void;
  onRemove: (entry: BisEntry) => void;
  onMove: (entry: BisEntry, dir: -1 | 1) => void;
  onClose: () => void;
}) {
  const equippedIds = new Set(equipped.map((p) => String(p.item_id)));
  const seats = seating?.seats ?? [];
  const spans = seating?.spans ?? false;
  /** Entry id → the socket it was seated in, so rows can say where they land. */
  const seatLabelByEntryId = new Map<string, string>();
  if (spans && seats[0]) {
    seatLabelByEntryId.set(seats[0].id, group.sockets.map((s) => s.label).join(" + "));
  } else {
    seats.forEach((entry, i) => {
      if (entry) seatLabelByEntryId.set(entry.id, group.sockets[i].label);
    });
  }
  const filledSeats = spans ? group.sockets.length : seats.filter(Boolean).length;

  // Weapons hold one pool for two sockets that accept different things, so the
  // picker offers a "fits where" narrowing. Rings don't need it — either
  // socket takes any ring.
  const socketsDiffer = group.sockets.some(
    (s) => JSON.stringify(s.accepts ?? group.itemSlots) !== JSON.stringify(group.sockets[0].accepts ?? group.itemSlots)
  );
  const [socketFilter, setSocketFilter] = useState<string | null>(null);
  useEffect(() => setSocketFilter(null), [group.id]);
  const activeSocket = socketFilter ? group.sockets.find((s) => s.key === socketFilter) ?? null : null;
  const shownItems = activeSocket
    ? items.filter((it) => socketAcceptsItem(group, activeSocket, it.slot))
    : items;

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center p-3 sm:p-6">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px] animate-in fade-in" onClick={onClose} />
      <div
        role="dialog"
        aria-label={`${group.label} slot`}
        className="relative flex h-full w-full max-w-[980px] flex-col overflow-hidden rounded-2xl border border-[#3a4351] bg-[linear-gradient(165deg,#141922_0%,#0c0f15_45%,#08090d_100%)] shadow-[0_30px_90px_rgba(0,0,0,0.75)] animate-in zoom-in-95 duration-150"
      >
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-white/[0.08] bg-black/30 px-4 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="truncate text-[19px] font-bold tracking-tight text-white">{group.label}</h4>
              {group.sockets.length > 1 ? (
                <span className="rounded-md border border-white/12 bg-white/[0.04] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-white/55">
                  {filledSeats}/{group.sockets.length} filled
                </span>
              ) : null}
            </div>
            <p className="mt-0.5 truncate text-[12.5px] text-white/40">
              {entries.length === 0
                ? `No targets saved yet · ${group.sockets.length} socket${group.sockets.length === 1 ? "" : "s"} to fill`
                : spans
                  ? `${entries.length} saved · a two-hander covers both sockets`
                  : `${entries.length} target${entries.length === 1 ? "" : "s"} saved · the seated ones show on your character sheet`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[12.5px] font-semibold text-white/55 transition-colors hover:border-white/20 hover:bg-white/[0.08] hover:text-white"
          >
            Done
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[320px_minmax(0,1fr)]">
          {/* Saved targets, in the order they'll be read */}
          <div className="flex min-h-0 flex-col border-b border-white/[0.07] md:border-b-0 md:border-r">
            <div className="shrink-0 px-3.5 py-2.5 text-[11px] font-bold uppercase tracking-[0.16em] text-amber-500/70">
              Your targets
            </div>
            <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto overscroll-contain px-2.5 pb-3">
              {entries.length === 0 ? (
                <p className="px-2 py-8 text-center text-[12.5px] leading-relaxed text-white/35">
                  Nothing saved for this slot. Pick something from the loot table.
                </p>
              ) : (
                entries.map((entry, i) => (
                  <SavedTargetRow
                    key={entry.id}
                    entry={entry}
                    gameCode={gameCode}
                    seatLabel={seatLabelByEntryId.get(entry.id) ?? null}
                    multiSocket={group.sockets.length > 1}
                    wearing={equippedIds.has(String(entry.external_item_id))}
                    canEdit={canEdit}
                    busy={busyItemId === entry.loot_item_id || reorderBusy}
                    canMoveUp={i > 0 && entries[i - 1].tag === entry.tag}
                    canMoveDown={i < entries.length - 1 && entries[i + 1].tag === entry.tag}
                    onMove={(dir) => onMove(entry, dir)}
                    onChangeTag={(t) => onChangeTag(entry, t)}
                    onRemove={() => onRemove(entry)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Loot table, narrowed by raid then boss */}
          <div className="flex min-h-0 flex-col">
            {canEdit ? (
              <>
                <div className="shrink-0 space-y-2.5 border-b border-white/[0.07] bg-black/20 px-3.5 py-3">
                  <input
                    value={search}
                    onChange={(e) => onSearch(e.target.value)}
                    placeholder={`Search ${group.label.toLowerCase()} items or bosses…`}
                    className="w-full rounded-xl border border-[#2f3640] bg-black/45 px-3 py-2.5 text-[14px] text-white shadow-inner transition-colors placeholder:text-white/25 focus:border-amber-500/45 focus:outline-none focus:ring-1 focus:ring-amber-500/25"
                  />
                  {socketsDiffer ? (
                    <FilterChipRow
                      label="Fits"
                      options={group.sockets.map((s) => s.key)}
                      optionLabel={(key) => group.sockets.find((s) => s.key === key)?.label ?? key}
                      value={socketFilter}
                      allLabel="Either hand"
                      onChange={setSocketFilter}
                    />
                  ) : null}
                  <FilterChipRow
                    label="Raid"
                    options={raids}
                    value={raid}
                    allLabel="Any raid"
                    onChange={onRaid}
                  />
                  {raid ? (
                    <FilterChipRow
                      label="Boss"
                      options={bosses}
                      value={boss}
                      allLabel="Any boss"
                      onChange={onBoss}
                    />
                  ) : null}
                  <p className="text-[11.5px] text-white/35">
                    {catalogLoading
                      ? "Loading the loot table…"
                      : `${shownItems.length} of ${totalForSlot} ${className || "eligible"} item${totalForSlot === 1 ? "" : "s"}`}
                  </p>
                </div>
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain p-2.5">
                  {shownItems.map((item) => {
                    const existing = entryByItemId.get(item.id) || null;
                    return (
                      <PickerRow
                        key={item.id}
                        item={item}
                        gameCode={gameCode}
                        existing={existing}
                        busy={busyItemId === item.id}
                        onAdd={(tag) => onAdd(item, tag)}
                        onChangeTag={(t) => existing && onChangeTag(existing, t)}
                        onRemove={() => existing && onRemove(existing)}
                      />
                    );
                  })}
                  {!catalogLoading && shownItems.length === 0 && (
                    <p className="py-10 text-center text-[12.5px] text-white/30">
                      Nothing matches those filters.
                    </p>
                  )}
                </div>
              </>
            ) : (
              <p className="m-auto max-w-xs px-6 text-center text-[12.5px] leading-relaxed text-white/35">
                You're viewing someone else's list. Their targets are on the left.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Raid / boss chips. One row, wraps, "any" always first. */
function FilterChipRow({
  label,
  options,
  optionLabel,
  value,
  allLabel,
  onChange,
}: {
  label: string;
  options: string[];
  /** Chips whose value differs from their display text (socket keys). */
  optionLabel?: (opt: string) => string;
  value: string | null;
  allLabel: string;
  onChange: (v: string | null) => void;
}) {
  if (options.length <= 1 && !value) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-0.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/28">{label}</span>
      {[null, ...options].map((opt) => {
        const on = value === opt;
        return (
          <button
            key={opt ?? "__all"}
            type="button"
            onClick={() => onChange(opt)}
            className={`rounded-lg border px-2 py-1 text-[11.5px] font-semibold transition-colors ${
              on
                ? "border-amber-400/60 bg-amber-500/20 text-amber-100"
                : "border-white/10 bg-white/[0.03] text-white/50 hover:border-white/20 hover:bg-white/[0.07] hover:text-white"
            }`}
          >
            {opt === null ? allLabel : optionLabel ? optionLabel(opt) : opt}
          </button>
        );
      })}
    </div>
  );
}

/** One saved target inside the slot panel — ordered, retaggable, removable. */
function SavedTargetRow({
  entry,
  gameCode,
  seatLabel,
  multiSocket,
  wearing,
  canEdit,
  busy,
  canMoveUp,
  canMoveDown,
  onMove,
  onChangeTag,
  onRemove,
}: {
  entry: BisEntry;
  gameCode: string | null;
  /** Which socket this target was seated in, or null when it didn't fit. */
  seatLabel: string | null;
  /** Groups with two sockets name the socket; single-socket ones don't need to. */
  multiSocket: boolean;
  wearing: boolean;
  canEdit: boolean;
  busy: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (dir: -1 | 1) => void;
  onChangeTag: (tag: Tag) => void;
  onRemove: () => void;
}) {
  const qc = itemQualityHex(bisListRowAccentQualityKey(entry));
  const tag = TAG_STYLES[entry.tag];
  const seated = seatLabel !== null;

  return (
    <div
      className={`rounded-xl border px-2.5 py-2 transition-colors ${
        seated ? "border-amber-500/35 bg-amber-500/[0.06]" : "border-white/[0.08] bg-black/25"
      }`}
    >
      <div className="flex items-start gap-2.5">
        <ItemIcon
          externalItemId={entry.external_item_id}
          gameCode={gameCode}
          name={entry.item_name}
          qualityHex={qc}
          sizeClass="size-9"
        />
        <div className="min-w-0 flex-1">
          <a
            href={wowheadItemUrl(entry.external_item_id, gameCode)}
            target="_blank"
            rel="noopener noreferrer"
            data-wh-rename-link="false"
            className="gear-item-name block truncate text-[13.5px] font-semibold leading-tight hover:underline"
            style={{ color: qc }}
          >
            {entry.item_name}
          </a>
          <p className="mt-0.5 truncate text-[11.5px] text-white/50">{sourceLabel(entry)}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className={`rounded border px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wider ${tag.pill}`}>
              {tag.label}
            </span>
            {seated ? (
              <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wider text-amber-200/85">
                {multiSocket ? seatLabel : "On your sheet"}
              </span>
            ) : (
              <span className="rounded border border-white/12 bg-white/[0.04] px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wider text-white/45">
                Alternate
              </span>
            )}
            {wearing ? (
              <span className="rounded border border-emerald-500/35 bg-emerald-500/12 px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wider text-emerald-200/90">
                Wearing it
              </span>
            ) : null}
          </div>
        </div>
        {canEdit ? (
          <div className="flex shrink-0 flex-col items-center gap-0.5">
            <button
              type="button"
              onClick={() => onMove(-1)}
              disabled={!canMoveUp || busy}
              title={canMoveUp ? "Move up" : "Retag it to move past a stronger pick"}
              className="rounded border border-white/10 bg-white/[0.03] px-1.5 text-[11px] text-white/45 transition-colors hover:border-white/25 hover:text-white disabled:opacity-25"
            >
              ▲
            </button>
            <button
              type="button"
              onClick={() => onMove(1)}
              disabled={!canMoveDown || busy}
              title="Move down"
              className="rounded border border-white/10 bg-white/[0.03] px-1.5 text-[11px] text-white/45 transition-colors hover:border-white/25 hover:text-white disabled:opacity-25"
            >
              ▼
            </button>
          </div>
        ) : null}
      </div>

      {canEdit ? (
        <div className="mt-2 flex items-center gap-1.5">
          {ALL_TAGS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onChangeTag(t)}
              disabled={busy}
              className={`flex-1 rounded-lg border px-1.5 py-1.5 text-[10.5px] font-bold uppercase tracking-wider transition-colors ${
                entry.tag === t
                  ? TAG_STYLES[t].pill
                  : "border-[#454d5b] bg-white/[0.05] text-white/65 hover:border-white/35 hover:bg-white/[0.11] hover:text-white"
              } ${busy ? "cursor-wait opacity-50" : ""}`}
            >
              {TAG_STYLES[t].label}
            </button>
          ))}
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            title="Remove from list"
            className="shrink-0 rounded-lg border border-transparent p-1.5 text-white/28 transition-colors hover:border-red-500/20 hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
          >
            <svg className="size-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : null}
    </div>
  );
}


// ── Loot history ──────────────────────────────────────────────

function relativeDayLabel(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}

/** Awards land as epic more often than not — treat 4+ (or the word) as a highlight. */
function isEpicOrBetter(qualityKey: string): boolean {
  const n = Number(qualityKey);
  if (Number.isFinite(n)) return n >= 4;
  return ["epic", "legendary", "artifact"].includes(qualityKey.toLowerCase());
}

function lootOfficerNote(raw: LootAwardRow["raw"]): string | null {
  const obj =
    typeof raw === "string"
      ? (() => {
          try {
            return JSON.parse(raw) as Record<string, unknown>;
          } catch {
            return {};
          }
        })()
      : (raw as Record<string, unknown>) || {};
  return typeof obj.officer_note === "string" && obj.officer_note.trim() ? obj.officer_note : null;
}

function LootStat({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string | number;
  hint?: string | null;
  accent: string;
}) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-black/40 px-3 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
      <div className={`absolute inset-x-0 top-0 h-px ${accent}`} aria-hidden />
      <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/40">{label}</p>
      <p className="mt-0.5 text-[22px] font-black leading-none tabular-nums text-white">{value}</p>
      {hint ? <p className="mt-1 truncate text-[10px] text-white/35">{hint}</p> : null}
    </div>
  );
}

function LootTagBadges({ award }: { award: LootAwardRow }) {
  const tag = (award.bis_tag_at_award || "").toLowerCase();
  const tagStyle = tag === "bis" || tag === "upgrade" || tag === "sidegrade" ? TAG_STYLES[tag as Tag] : null;
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
      {tagStyle ? (
        <span
          className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${tagStyle.pill}`}
          title={`This item was tagged ${tagStyle.label} on the list when it was awarded`}
        >
          <span className={`size-1.5 rounded-full ${tagStyle.dot}`} aria-hidden />
          {tag === "bis" ? "BIS secured" : tagStyle.label}
        </span>
      ) : null}
      <span
        className="rounded-md border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white/40"
        title={award.source === "rclc" ? "Imported from an RCLootCouncil export" : "Recorded manually by an officer"}
      >
        {award.source === "rclc" ? "RCLC" : "Manual"}
      </span>
    </div>
  );
}

/** Big card for the most recent pickup — the "you won this" moment. */
function LatestDropCard({ award, gameCode }: { award: LootAwardRow; gameCode: string | null }) {
  const qualityKey = coerceLootQualityKey(award.item_quality) || "4";
  const qc = itemQualityHex(qualityKey);
  const when = award.awarded_at
    ? new Date(award.awarded_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : null;
  const rel = relativeDayLabel(award.awarded_at);
  const note = lootOfficerNote(award.raw);

  return (
    <div
      className="relative overflow-hidden rounded-2xl border p-4 shadow-[0_18px_50px_-30px_rgba(0,0,0,0.9)]"
      style={{
        borderColor: `${qc}55`,
        background: `linear-gradient(120deg, ${qc}1f 0%, rgba(8,9,13,0.85) 55%, rgba(6,7,10,0.95) 100%)`,
      }}
    >
      <span className="loot-sheen pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-transparent via-white/10 to-transparent" aria-hidden />
      <div className="relative flex flex-wrap items-center gap-4">
        <div className="relative shrink-0">
          <span
            className="absolute -inset-2 rounded-full blur-xl"
            style={{ background: `radial-gradient(circle, ${qc}44, transparent 70%)` }}
            aria-hidden
          />
          <ItemIcon
            externalItemId={award.external_item_id}
            gameCode={gameCode}
            name={award.item_name || undefined}
            qualityHex={qc}
            sizeClass="relative size-14"
            iconSize="large"
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-black uppercase tracking-[0.26em] text-amber-400/85">Latest pickup</p>
          <a
            href={wowheadItemUrl(award.external_item_id, gameCode)}
            target="_blank"
            rel="noopener noreferrer"
            data-wh-rename-link="false"
            className="gear-item-name mt-0.5 block truncate text-[20px] font-black leading-tight tracking-tight hover:underline"
            style={{ color: qc }}
          >
            {award.item_name || `#${award.external_item_id}`}
          </a>
          <p className="mt-1 truncate text-[11px] text-white/45">
            {rel ? <span className="font-semibold text-white/70">{rel}</span> : null}
            {rel && when ? <span className="text-white/20"> · </span> : null}
            {when}
          </p>
        </div>
        <LootTagBadges award={award} />
      </div>
      {award.response || note ? (
        <div className="relative mt-3 space-y-1 border-t border-white/[0.07] pt-2.5 text-[11px]">
          {award.response ? (
            <p className="text-white/45">
              <span className="text-white/30">Response</span> {award.response}
            </p>
          ) : null}
          {note ? <p className="border-l-2 border-violet-400/40 pl-2 italic text-white/50">“{note}”</p> : null}
        </div>
      ) : null}
    </div>
  );
}

function LootRow({ award, gameCode }: { award: LootAwardRow; gameCode: string | null }) {
  const qualityKey = coerceLootQualityKey(award.item_quality) || "4";
  const qc = itemQualityHex(qualityKey);
  const epic = isEpicOrBetter(qualityKey);
  const note = lootOfficerNote(award.raw);
  const time = award.awarded_at
    ? new Date(award.awarded_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <li
      className="group/loot rounded-xl border border-white/[0.07] bg-black/30 px-3 py-2 transition-colors hover:border-white/[0.16] hover:bg-black/45"
      style={{ borderLeftWidth: 3, borderLeftColor: qc }}
    >
      <div className="flex items-center gap-3">
        <span className="relative shrink-0">
          {epic ? (
            <span
              className="absolute -inset-1 rounded-lg blur-md"
              style={{ background: `radial-gradient(circle, ${qc}33, transparent 70%)` }}
              aria-hidden
            />
          ) : null}
          <ItemIcon
            externalItemId={award.external_item_id}
            gameCode={gameCode}
            name={award.item_name || undefined}
            qualityHex={qc}
            sizeClass="relative size-10"
          />
        </span>
        <div className="min-w-0 flex-1">
          <a
            href={wowheadItemUrl(award.external_item_id, gameCode)}
            target="_blank"
            rel="noopener noreferrer"
            data-wh-rename-link="false"
            className="gear-item-name block truncate text-[13px] font-bold leading-tight tracking-tight hover:underline"
            style={{ color: qc }}
          >
            {award.item_name || `#${award.external_item_id}`}
          </a>
          <p className="mt-0.5 truncate text-[10px] text-white/38">
            {time ?? "Time unknown"}
            {award.response ? <span className="text-white/20"> · {award.response}</span> : null}
          </p>
        </div>
        <LootTagBadges award={award} />
      </div>
      {note ? (
        <p className="mt-2 border-l-2 border-violet-400/35 pl-2 text-[10px] italic leading-snug text-white/45">“{note}”</p>
      ) : null}
    </li>
  );
}

/** Loot tab: a raid-night killfeed rather than a flat list. */
function LootHistoryPanel({ awards, gameCode }: { awards: LootAwardRow[]; gameCode: string | null }) {
  const sorted = useMemo(
    () => [...awards].sort((a, b) => (b.awarded_at || "").localeCompare(a.awarded_at || "")),
    [awards]
  );

  const stats = useMemo(() => {
    let bis = 0;
    let epic = 0;
    for (const a of sorted) {
      if ((a.bis_tag_at_award || "").toLowerCase() === "bis") bis++;
      if (isEpicOrBetter(coerceLootQualityKey(a.item_quality) || "4")) epic++;
    }
    return { total: sorted.length, bis, epic };
  }, [sorted]);

  const latest = sorted[0] ?? null;

  // Everything below the hero card, bucketed by raid night. Anything before
  // 05:00 local belongs to the night that started the evening before, so a
  // pull that runs past midnight stays in one group.
  const nights = useMemo(() => {
    const out: { key: string; label: string; items: LootAwardRow[] }[] = [];
    for (const a of sorted.slice(1)) {
      const raw = a.awarded_at ? new Date(a.awarded_at) : null;
      const valid = raw && !Number.isNaN(raw.getTime());
      const d = valid ? new Date(raw!.getTime() - 5 * 60 * 60 * 1000) : null;
      const key = d ? `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}` : "unknown";
      const label = d
        ? d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })
        : "Date unknown";
      const last = out[out.length - 1];
      if (last && last.key === key) last.items.push(a);
      else out.push({ key, label, items: [a] });
    }
    return out;
  }, [sorted]);

  return (
    <div className="mx-auto max-w-3xl space-y-4 pb-8">
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <LootStat label="Drops won" value={stats.total} accent="bg-gradient-to-r from-violet-500/70 to-transparent" />
        <LootStat
          label="BIS secured"
          value={stats.bis}
          hint={stats.total > 0 ? `${Math.round((stats.bis / stats.total) * 100)}% of awards` : null}
          accent="bg-gradient-to-r from-amber-500/80 to-transparent"
        />
        <LootStat label="Epic or better" value={stats.epic} accent="bg-gradient-to-r from-fuchsia-500/70 to-transparent" />
      </div>

      {latest ? <LatestDropCard award={latest} gameCode={gameCode} /> : null}

      {nights.length > 0 ? (
        <div className="relative pl-5">
          <span
            className="absolute bottom-2 left-[7px] top-2 w-px bg-gradient-to-b from-amber-500/45 via-white/10 to-transparent"
            aria-hidden
          />
          {nights.map((night) => (
            <div key={night.key} className="mb-4 last:mb-0">
              <div className="relative mb-2 flex items-center gap-2">
                <span
                  className="absolute -left-[18px] size-2.5 rounded-full bg-amber-400 ring-4 ring-amber-500/15"
                  aria-hidden
                />
                <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/60">{night.label}</span>
                <span className="rounded-md border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[9px] font-bold tabular-nums text-white/45">
                  {night.items.length} drop{night.items.length === 1 ? "" : "s"}
                </span>
              </div>
              <ul className="space-y-1.5">
                {night.items.map((a) => (
                  <LootRow key={a.id} award={a} gameCode={gameCode} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PickerRow({
  item,
  gameCode,
  existing,
  busy,
  onAdd,
  onChangeTag,
  onRemove,
}: {
  item: LootItem;
  gameCode: string | null;
  existing: BisEntry | null;
  busy: boolean;
  onAdd: (tag: Tag) => void;
  onChangeTag: (tag: Tag) => void;
  onRemove: () => void;
}) {
  const nameColor = itemQualityHex(lootQualityKeyFromLootItem(item));
  return (
    <div
      className={`rounded-xl border px-3 py-2.5 sm:py-3 transition-colors ${
        existing
          ? `bg-black/50 border-[#3f4652] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ring-1 ring-inset ${TAG_STYLES[existing.tag].ring}/25`
          : "bg-black/25 border-[#2f3640]/90 hover:border-white/25 hover:bg-black/35 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <ItemIcon
          externalItemId={item.external_item_id}
          gameCode={gameCode}
          name={item.name}
          qualityHex={nameColor}
          sizeClass="size-9"
        />
        <div className="min-w-0 flex-1 space-y-0.5">
          <a
            href={wowheadItemUrl(item.external_item_id, gameCode)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[13.5px] font-semibold truncate block underline-offset-2 decoration-from-font decoration-current/35 hover:underline hover:decoration-current/55"
            style={{ color: nameColor }}
            data-wh-rename-link="false"
          >
            {item.name}
          </a>
          <div className="truncate text-[11.5px] font-medium text-white/52">{sourceLabel(item)}</div>
          {item.redeemed_from_name && (
            <div className="text-[11px] text-amber-200/65 truncate flex items-center gap-1 font-medium" title={`Token drops from ${item.redeemed_from_boss || "unknown boss"}`}>
              <span aria-hidden className="opacity-60 shrink-0">↻</span>
              <span className="truncate">Trade in: {item.redeemed_from_name}</span>
            </div>
          )}
        </div>
        {existing && (
          <button
            onClick={onRemove}
            disabled={busy}
            title="Remove"
            className="shrink-0 rounded-lg border border-transparent p-1.5 text-white/28 hover:text-red-400 hover:bg-red-500/10 hover:border-red-500/20 disabled:opacity-50 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      <div className="flex gap-2 sm:gap-1.5">
        {ALL_TAGS.map((t) => {
          const isCurrent = existing?.tag === t;
          return (
            <button
              key={t}
              onClick={() => (existing ? onChangeTag(t) : onAdd(t))}
              disabled={busy}
              className={`
                flex-1 min-w-0 min-h-[44px] sm:min-h-0 px-2 py-2.5 sm:px-2 sm:py-2 rounded-lg text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.06em] border transition-all inline-flex items-center justify-center
                ${isCurrent
                  ? TAG_STYLES[t].pill
                  : "border-[#454d5b] bg-white/[0.05] text-white/70 hover:border-white/35 hover:bg-white/[0.11] hover:text-white"
                }
                ${busy ? "opacity-50 cursor-wait" : "cursor-pointer"}
              `}
            >
              {existing ? (isCurrent ? `✓ ${TAG_STYLES[t].label}` : TAG_STYLES[t].label) : `+ ${TAG_STYLES[t].label}`}
            </button>
          );
        })}
      </div>
    </div>
  );
}
